// assets/stamp-designer.js
// <stamp-designer-component> — a freeform, VistaPrint-style stamp editor built
// on Fabric.js. Replaces the old fixed-slot stamp-customizer as the "design"
// step of the studio wizard, while preserving every hand-off contract:
//   - writes hidden <input name="properties[...]"> bound to the product form
//   - dispatches a bubbling `stamp:proof-confirmed` (the studio's progress gate)
//   - dispatches a bubbling `stamp:design-changed` {detail:{textLength}} (advisory)
//   - honours ?template=<id> and ?mode=upload
//
// Single-ink by nature: the whole design previews + prints in one ink colour.
// On proof-confirm the scene is exported to a production SVG die with all text
// OUTLINED to vector <path> (via opentype.js + vendored TTFs) so the die matches
// the on-screen preview regardless of the manufacturer's installed fonts.
import { Component } from '@theme/component';
import { Canvas, IText, FabricImage, Rect, Circle, Line, Path, Group, Control, Point, util } from 'fabric';
import opentype from 'opentype';
import {
  SD_FONTS, SD_INKS, SD_ICONS, SD_BORDERS,
  getFont, getInk, getIcon, getBorder, fontFaceCSS, resolveTtfUrl,
} from '@theme/stamp-designer-assets';
import { getTemplate, templateToScene } from '@theme/stamp-templates';

// On-screen canvas is a fixed square; the die boundary is drawn to the chosen
// size's real aspect ratio inside the padded design area.
const CANVAS = 520;
const PADDING = 36;
const AREA = CANVAS - PADDING * 2;         // max design-area extent
const CENTER = CANVAS / 2;
const SAFE_INSET = 0.06;                    // safe-margin guide as fraction of area
const MAX_UNDO = 25;
const PROP_LIMIT = 60000;                   // Shopify line-item-property size guard
// Flat default for template-placed plain text (centre / rectangle line / "for"
// label) -- matches the freeform addText() default. Arc/ring text is
// deliberately NOT included: it already has its own dedicated, auto-fit
// sizing (see #ringSpanLimitDeg/#fitRingFontSize) tuned to avoid clipping
// behind a star, and flattening it here would undo that.
const TEXT_BASE_SIZE = 34;

// Demo/provision feature (emailDesign): sends the finalized production SVG to
// a fixed test address via EmailJS (vendored, see emailjs.js -- no backend
// exists in this theme, so a client-callable email service is the only way
// to send real mail from here). These three IDs come from the EmailJS
// dashboard (Account > API Keys, and the Service/Template you create there)
// and are NOT secret -- EmailJS's public key is meant to be used client-side;
// the recipient itself is fixed in the EmailJS template config, not passed
// from here. Fill these in once the EmailJS account/template exist.
const SD_EMAIL_SERVICE_ID = 'service_4f072ki';
const SD_EMAIL_TEMPLATE_ID = 'template_b13u373';
const SD_EMAIL_PUBLIC_KEY = 'OU_8-H1XZLnlU2YNh';

// Custom object props carried through Fabric (de)serialization.
const EXTRA_PROPS = [
  'sdType', 'fontId', 'sdWeight', 'sdText', 'sdFontSize', 'sdRadius', 'sdArc', 'sdSpacing', 'sdFlatten', 'sdIconId',
  'sdBorderId', 'sdDefaultW', 'sdDefaultH', 'sdOrigSrc', 'sdThreshold', 'sdMode', 'sdGlyphs', 'sdCenter', 'sdUniform', 'sdNoResize', 'sdDateBand', 'selectable', 'perPixelTargetFind',
  // The date band is only fixed because of these. toObject() drops anything
  // not listed here, so without them one undo handed the shopper back a
  // movable, editable, resizable set of date wheels -- verified: after a
  // single undo/redo the band came back evented, editable and unlocked.
  'evented', 'editable', 'hasControls', 'hasBorders',
  'lockMovementX', 'lockMovementY', 'lockScalingX', 'lockScalingY', 'lockRotation',
];

// Matches Fabric's own internal touch check in this vendored build (a stylus
// is treated like a mouse, same as Fabric does) -- used to decide, per
// gesture, whether round-die text should lose its drag-resize handles and
// whether the marquee/rubber-band multi-select rectangle should fire.
const TOUCH_EVENT_TYPES = new Set(['touchstart', 'touchmove', 'touchend']);
function isTouchLikeEvent(e) {
  return !!e && (TOUCH_EVENT_TYPES.has(e.type) || e.pointerType === 'touch');
}

class StampDesigner extends Component {
  #canvas = null;
  #shape = 'round';                 // 'round' | 'rectangle'
  #area = null;                     // {cx,cy,w,h,radius?} boundary geometry (canvas px)
  #ink = SD_INKS[0];
  #size = null;                     // {label,...} set by the studio via setStampSize()
  #assets = {};                     // font asset-url map injected by the block
  #otCache = new Map();             // fontId-weight -> opentype.Font
  #undo = [];
  #redo = [];
  #loading = false;                 // suppress snapshots during programmatic loads
  #proofConfirmed = false;
  #emitTimer = 0;
  #snapLines = [];
  #propInputs = null;
  #mctx = null;
  #pendingScene = null;
  #pendingUploadIntro = false;
  #ro = null;
  #onKey = null;
  #nudgeTimer = 0;
  #clipboard = null;
  #snapLevel = 'basic';        // 'basic' (die+centre) | 'figma' (+ other elements)
  #vGuide = null;
  #hGuide = null;
  #lineMode = false;                // MS-Paint line tool: press-drag to draw
  #lineDraft = null;                // the Line being drawn this drag
  #lineStart = null;                // {x,y} scene anchor of the current draw
  #lastInputWasTouch = false;       // decided fresh every gesture, in mouse:down:before
  #preDownActive = null;            // object that was active BEFORE the current mousedown
  #touchStartX = 0;
  #touchStartY = 0;
  #touchMoved = false;               // real physical displacement this gesture (see #bindCanvasEvents)
  #curvedEditTarget = null;         // the curvedText object the inline overlay is editing
  #curvedEditCleanup = null;        // removes the overlay's own listeners on close

  async connectedCallback() {
    super.connectedCallback();
    try {
      this.#snapLevel = this.dataset.snapLevel === 'figma' ? 'figma' : 'basic';
      this.#readAssets();
      this.#injectFontFaces();
      this.#initCanvas();
      this.#applyInkSwatches();
      this.#buildControls();
      // Default shape until the studio drives it; round is the common seal case.
      this.setStampSize({ shape: 'round', label: '' });
      this.#bindCanvasEvents();
      await this.#preloadFonts();
      this.#applyLaunchParams();
      this.#snapshot();               // baseline for undo
      this.#dispatchChanged();
      this.#setupResize();
      this.#fitToContainer();
      this.#onKey = (e) => this.#handleKey(e);
      document.addEventListener('keydown', this.#onKey);
    } catch (e) {
      console.error('[stamp-designer] init failed', e);
    }
  }

  // ---------------------------------------------------------------- assets ---
  #readAssets() {
    const node = this.querySelector('[data-sd-assets]');
    if (node) {
      try { this.#assets = JSON.parse(node.textContent || '{}'); }
      catch (e) { console.warn('[stamp-designer] bad asset map', e); this.#assets = {}; }
    }
  }

  #injectFontFaces() {
    const fonts = this.#assets.fonts || {};
    const css = fontFaceCSS(fonts);
    if (!css) return;
    const style = document.createElement('style');
    style.setAttribute('data-sd-fontface', '');
    style.textContent = css;
    document.head.appendChild(style);
  }

  async #preloadFonts() {
    if (!document.fonts) return;
    const jobs = [];
    for (const f of SD_FONTS) {
      for (const w of f.weights) {
        jobs.push(document.fonts.load(`${w} 24px '${f.cssFamily}'`).catch(() => {}));
      }
    }
    await Promise.all(jobs);
    await document.fonts.ready;
    this.#canvas?.requestRenderAll();
  }

  // ---------------------------------------------------------------- canvas ---
  #initCanvas() {
    const el = this.refs.canvas || this.querySelector('canvas');
    if (!el) throw new Error('no <canvas>');
    this.#canvas = new Canvas(el, {
      width: CANVAS,
      height: CANVAS,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      selection: true,
      targetFindTolerance: 6,      // makes thin strokes easy to click with per-pixel find
      // Fabric's own mechanism for "swipe scrolls the page unless you're
      // dragging the object that's already selected": on touch, its
      // _onTouchStart only captures the gesture (preventDefault) when the
      // touched target is the object that was ALREADY active before this
      // touch began -- otherwise it lets the browser's native scroll take
      // over, and doesn't move the object either. Verified directly against
      // this vendored build (event.defaultPrevented + object position,
      // before/after selecting), not just from the option's name.
      allowTouchScrolling: true,
    });
  }

  /**
   * Configure the die boundary for a physical size. Called by the studio.
   * @param {{shape:'round'|'rectangle', label?:string, mm?:object}} size
   */
  setStampSize(size) {
    if (!size) return;
    this.#size = size;
    this.#shape = size.shape === 'rectangle' ? 'rectangle' : 'round';
    let w = AREA, h = AREA;
    if (this.#shape === 'rectangle' && size.mm && size.mm.width && size.mm.height) {
      const ar = size.mm.width / size.mm.height;
      if (ar >= 1) { w = AREA; h = AREA / ar; } else { h = AREA; w = AREA * ar; }
    }
    this.#area = this.#shape === 'round'
      ? { cx: CENTER, cy: CENTER, w: AREA, h: AREA, radius: AREA / 2 }
      : { cx: CENTER, cy: CENTER, w, h };
    // Curved text (tool + its Curveness control) is round-only complexity we're
    // retiring for round dies specifically -- ring/arc text from a loaded template
    // still renders fine, it just can't be created fresh or have its curve tweaked.
    if (this.refs.curvedTextBtn) this.refs.curvedTextBtn.hidden = this.#shape === 'round';
    this.#syncBorderButton();
    this.#buildGuides();
    this.#reclipAll();
    this.#canvas?.requestRenderAll();
    this.#updateDimLabels();
    this.#fitToContainer();
    // When the studio picks the real physical size, re-place a pending template
    // scene so it fits the true die aspect ratio (rectangles especially).
    if (this.#pendingScene && size.label) {
      const sc = this.#pendingScene;
      this.#pendingScene = null;
      this.#applyTemplateScene(sc);
    }
    // First size pick after arriving via the upload entry point: show the
    // guidelines/welcome card instead of the bare canvas+toolbar.
    if (this.#pendingUploadIntro && size.label) {
      this.#pendingUploadIntro = false;
      this.startUploadFlow();
    }
  }

  /** Non-selectable die boundary + safe-margin guides (excluded from export). */
  #buildGuides() {
    if (!this.#canvas) return;
    this.#canvas.getObjects().filter((o) => o.sdGuide).forEach((o) => this.#canvas.remove(o));
    this.#vGuide = null; this.#hGuide = null;   // snap guides are sdGuide too; recreated on demand
    const a = this.#area;
    const common = { selectable: false, evented: false, excludeFromExport: true, sdGuide: true, hoverCursor: 'default' };
    const add = [];
    if (this.#shape === 'round') {
      // No boundary guide circle for round dies at all (per user request) -- the
      // border's own outer ring (see #buildBorderGroup/addBorder, flush with the
      // true edge) is the only edge reference now, and it's locked/always-there
      // once placed. #snapTargets still computes the same safe-margin snap lines
      // invisibly, straight off this.#area, independent of any guide object.
    } else {
      add.push(new Rect({ ...common, left: a.cx, top: a.cy, originX: 'center', originY: 'center', width: a.w, height: a.h, fill: '', stroke: '#c9bfae', strokeWidth: 1.5 }));
      // Dashed safe-margin guide removed per user request -- #snapTargets still
      // computes the same safe-margin snap lines invisibly, straight off
      // this.#area/SAFE_INSET, independent of this guide object.
    }
    add.forEach((o) => this.#canvas.add(o));
    this.#sinkGuides();
  }

  #sinkGuides() {
    this.#canvas?.getObjects().filter((o) => o.sdGuide).forEach((o) => this.#canvas.sendObjectToBack(o));
  }

  #userObjects() {
    return this.#canvas ? this.#canvas.getObjects().filter((o) => !o.sdGuide) : [];
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#ro?.disconnect();
    this.#ro = null;
    if (this.#onKey) document.removeEventListener('keydown', this.#onKey);
    this.#onKey = null;
  }

  #setupResize() {
    if (this.#ro || typeof ResizeObserver === 'undefined') return;
    const target = this.refs.stage;
    if (!target) return;
    this.#ro = new ResizeObserver(() => this.#fitToContainer());
    this.#ro.observe(target);
  }

  /**
   * Scale the on-screen canvas to fit its column WITHOUT changing the internal
   * 520x520 resolution (keeps export crisp), so the whole die is visible on
   * arrival. Refires via ResizeObserver when the design panel becomes visible.
   */
  #fitToContainer() {
    if (!this.#canvas) return;
    const box = this.refs.canvasBox || this.refs.stage;
    const avail = box ? box.clientWidth : 0;
    if (!avail) return;
    const size = Math.max(200, Math.min(Math.round(avail), CANVAS));
    this.#canvas.setDimensions({ width: `${size}px`, height: `${size}px` }, { cssOnly: true });
    this.#positionDimLabels();
  }

  /** A clip shape (canvas coords) matching the die, for clipping user objects. */
  #dieClip() {
    const a = this.#area;
    if (!a) return null;
    const common = { absolutePositioned: true, originX: 'center', originY: 'center', left: a.cx, top: a.cy };
    return this.#shape === 'round'
      ? new Circle({ ...common, radius: a.radius })
      : new Rect({ ...common, width: a.w, height: a.h });
  }

  /** Clip a user object to the die — except borders (the die's own frame). */
  #clipFor(obj) {
    return obj && obj.sdType === 'border' ? null : this.#dieClip();
  }

  #reclipAll() {
    this.#userObjects().forEach((o) => { o.clipPath = this.#clipFor(o); o.dirty = true; });
  }

  /** Position the size labels along the actual die edges (not the square canvas). */
  #positionDimLabels() {
    const r = this.refs;
    const a = this.#area;
    const el = this.refs.canvas;
    if (!a || !el) return;
    const disp = el.clientWidth || CANVAS;
    const scale = disp / CANVAS;
    const dieW = a.w * scale, dieH = a.h * scale;
    const dieLeft = (disp - dieW) / 2, dieTop = (disp - dieH) / 2;
    if (r.dimW) {
      r.dimW.style.left = `${dieLeft + dieW / 2}px`;
      r.dimW.style.top = `${dieTop + dieH + 7}px`;
    }
    if (r.dimH && !r.dimH.hidden) {
      r.dimH.style.left = `${dieLeft - 7}px`;
      r.dimH.style.top = `${dieTop + dieH / 2}px`;
    }
  }

  /** VistaPrint-style physical-size labels around the preview. */
  #updateDimLabels() {
    const r = this.refs;
    const s = this.#size;
    const cm = (mm) => `${(mm / 10).toFixed(1).replace(/\.0$/, '')} cm`;
    if (!s || !s.mm) {
      if (r.dimW) r.dimW.textContent = '';
      if (r.dimH) r.dimH.hidden = true;
      return;
    }
    if (this.#shape === 'round' && s.mm.diameter) {
      if (r.dimW) r.dimW.textContent = `⌀ ${cm(s.mm.diameter)}`;
      if (r.dimH) r.dimH.hidden = true;
    } else if (s.mm.width && s.mm.height) {
      if (r.dimW) r.dimW.textContent = cm(s.mm.width);
      if (r.dimH) { r.dimH.hidden = false; r.dimH.textContent = cm(s.mm.height); }
    }
    this.#positionDimLabels();
  }

  // ------------------------------------------------------------- add: text ---
  addText() {
    const font = SD_FONTS[0];
    const t = new IText('Your text', {
      left: this.#area.cx, top: this.#area.cy, originX: 'center', originY: 'center',
      fontFamily: font.cssFamily, fontWeight: 400, fontSize: 34, fill: this.#ink.hex,
      textAlign: 'center', charSpacing: 0, sdType: 'text', fontId: font.id, sdWeight: 400,
      sdUniform: true, sdNoResize: this.#shape === 'round', perPixelTargetFind: this.#shape === 'round',
    });
    this.#addObject(t, true);
    t.enterEditing?.();
    t.selectAll?.();
  }

  // ---------------------------------------------------------- add: shapes ---
  addRect() {
    const a = this.#area, s = Math.min(a.w, a.h) * 0.5;
    this.#addObject(new Rect({ left: a.cx, top: a.cy, originX: 'center', originY: 'center', width: s, height: s * 0.62, fill: '', stroke: this.#ink.hex, strokeWidth: 3, sdType: 'shape' }), true);
  }
  addCircle() {
    const a = this.#area, r = Math.min(a.w, a.h) * 0.28;
    this.#addObject(new Circle({ left: a.cx, top: a.cy, originX: 'center', originY: 'center', radius: r, fill: '', stroke: this.#ink.hex, strokeWidth: 3, sdType: 'shape' }), true);
  }
  // -------------------------------------------------------- add: line (MS Paint) ---
  /**
   * Toggle the MS-Paint line tool. While active the cursor is a crosshair and a
   * press-drag on the canvas rubber-bands a straight line (Shift constrains to
   * 0/45/90°); releasing drops it and hands back to the normal select tool. The
   * dropped line keeps two draggable endpoint handles for later fine-tuning.
   */
  startLine() {
    if (this.#lineMode) { this.#exitLineMode(); return; }
    this.#lineMode = true;
    this.#canvas.discardActiveObject();
    this.#canvas.selection = false;
    // allowTouchScrolling makes Fabric hand a touch-drag entirely to the
    // browser (no mouse:move at all) whenever it doesn't start on an
    // already-active object -- which describes every line-draw gesture by
    // definition (you press on empty canvas to start one). Off for the
    // duration of line mode so drawing still works on touch; restored on exit.
    this.#canvas.allowTouchScrolling = false;
    this.#canvas.skipTargetFind = true;   // press-drag only draws; never grabs an element
    this.#canvas.defaultCursor = 'crosshair';
    this.#canvas.hoverCursor = 'crosshair';
    this.#canvas.requestRenderAll();
    this.#syncInspector();
    this.#setLineButtonActive(true);
  }

  #exitLineMode() {
    if (!this.#lineMode) return;
    // discard an unfinished draft (e.g. Esc mid-drag)
    if (this.#lineDraft) { this.#canvas.remove(this.#lineDraft); this.#lineDraft = null; }
    this.#lineMode = false;
    this.#canvas.selection = true;
    this.#canvas.allowTouchScrolling = true;
    this.#canvas.skipTargetFind = false;
    this.#canvas.defaultCursor = 'default';
    this.#canvas.hoverCursor = 'move';
    this.#setGuides([]);
    this.#canvas.requestRenderAll();
    this.#setLineButtonActive(false);
  }

  #setLineButtonActive(on) {
    this.refs.lineBtn?.classList.toggle('is-active', on);
  }

  /** Clamp a scene point to inside the die (round: circle, rect: box). */
  #clampToDie(x, y) {
    const a = this.#area; if (!a) return { x, y };
    if (this.#shape === 'round') {
      const dx = x - a.cx, dy = y - a.cy, d = Math.hypot(dx, dy), max = a.radius;
      if (d > max && d > 0) return { x: a.cx + (dx / d) * max, y: a.cy + (dy / d) * max };
      return { x, y };
    }
    const hw = a.w / 2, hh = a.h / 2;
    return { x: Math.max(a.cx - hw, Math.min(a.cx + hw, x)), y: Math.max(a.cy - hh, Math.min(a.cy + hh, y)) };
  }

  #onLineDown(e) {
    if (!this.#lineMode) return;
    const p = this.#clampToDie(...this.#scenePoint(e));
    const w = Math.max(1, Math.min(30, parseInt(this.refs.strokeInput?.value, 10) || 3));
    const line = new Line([p.x, p.y, p.x, p.y], {
      stroke: this.#ink.hex, strokeWidth: w, sdType: 'shape', strokeUniform: true,
      originX: 'center', originY: 'center', left: p.x, top: p.y, objectCaching: false,
    });
    this.#lineStart = p;
    this.#lineDraft = line;
    this.#canvas.add(line);
    this.#canvas.requestRenderAll();
  }

  #onLineMove(e) {
    if (!this.#lineMode || !this.#lineDraft) return;
    let p = this.#clampToDie(...this.#scenePoint(e));
    const s = this.#lineStart;
    const guides = [];
    if (e.e && e.e.shiftKey) {
      // Shift → snap the segment to the nearest 45°.
      const dx = p.x - s.x, dy = p.y - s.y, len = Math.hypot(dx, dy);
      const step = Math.PI / 4;
      const ang = Math.round(Math.atan2(dy, dx) / step) * step;
      p = this.#clampToDie(s.x + Math.cos(ang) * len, s.y + Math.sin(ang) * len);
      if (Math.abs(Math.sin(ang)) < 1e-6) guides.push({ axis: 'h', pos: s.y });
      else if (Math.abs(Math.cos(ang)) < 1e-6) guides.push({ axis: 'v', pos: s.x });
    }
    const l = this.#lineDraft;
    l.set({
      x1: s.x, y1: s.y, x2: p.x, y2: p.y,
      left: (s.x + p.x) / 2, top: (s.y + p.y) / 2,
      width: Math.abs(p.x - s.x), height: Math.abs(p.y - s.y),
    });
    l.setCoords();
    this.#setGuides(guides);
    this.#canvas.requestRenderAll();
  }

  #onLineUp() {
    if (!this.#lineMode || !this.#lineDraft) return;
    const l = this.#lineDraft;
    this.#lineDraft = null;
    const len = Math.hypot((l.x2 - l.x1), (l.y2 - l.y1));
    if (len < 3) {
      // a click with no drag — nothing to draw
      this.#canvas.remove(l);
      this.#exitLineMode();
      return;
    }
    this.#applyLineControls(l);
    l.clipPath = this.#clipFor(l);
    this.#exitLineMode();
    this.#canvas.setActiveObject(l);
    this.#canvas.requestRenderAll();
    this.#snapshot();
    this.#dispatchChanged();
    this.#syncInspector();
    this.#scheduleEmit();
  }

  /** Scene-space pointer for a Fabric mouse event (accounts for zoom/pan). */
  #scenePoint(e) {
    const p = this.#canvas.getScenePoint(e.e);
    return [p.x, p.y];
  }

  /**
   * Two draggable endpoint handles reshape a line; body-drag moves it. Uses the
   * line's centre origin so the control math matches Fabric's rendering without
   * recomputing the bbox each frame. Applied to every line (drawn or template).
   */
  #applyLineControls(line) {
    line.hasBorders = false;
    line.lockScalingX = true; line.lockScalingY = true; line.lockRotation = true;
    line.objectCaching = false;
    // centre origin keeps calcLinePoints (±w/2, ±h/2) consistent with left/top.
    line.set({ originX: 'center', originY: 'center', left: (line.x1 + line.x2) / 2, top: (line.y1 + line.y2) / 2 });
    line.setCoords();
    const endpoint = (isP1) => new Control({
      actionName: 'sdLineEndpoint',
      positionHandler: (dim, finalMatrix, o) => {
        const lp = o.calcLinePoints();
        const pt = new Point(isP1 ? lp.x1 : lp.x2, isP1 ? lp.y1 : lp.y2);
        return pt.transform(util.multiplyTransformMatrices(o.canvas.viewportTransform, o.calcTransformMatrix()));
      },
      actionHandler: (eventData, transform, x, y) => {
        const l = transform.target;
        const lp = l.calcLinePoints();
        const m = l.calcTransformMatrix();
        const s1 = new Point(lp.x1, lp.y1).transform(m);
        const s2 = new Point(lp.x2, lp.y2).transform(m);
        const fixed = isP1 ? s2 : s1;           // the endpoint that isn't being dragged
        let dx = x, dy = y;
        const guides = [];
        // snap the line to horizontal / vertical (Alt bypasses)
        if (!eventData.altKey) {
          const ang = (((Math.atan2(dy - fixed.y, dx - fixed.x) * 180) / Math.PI) % 180 + 180) % 180;
          const tol = 4;
          if (ang < tol || ang > 180 - tol) { dy = fixed.y; guides.push({ axis: 'h', pos: fixed.y }); }
          else if (Math.abs(ang - 90) < tol) { dx = fixed.x; guides.push({ axis: 'v', pos: fixed.x }); }
        }
        const d = new Point(dx, dy);
        const np1 = isP1 ? d : s1;
        const np2 = isP1 ? s2 : d;
        l.set({
          x1: np1.x, y1: np1.y, x2: np2.x, y2: np2.y,
          left: (np1.x + np2.x) / 2, top: (np1.y + np2.y) / 2,
          width: Math.abs(np2.x - np1.x), height: Math.abs(np2.y - np1.y),
        });
        l.setCoords();
        this.#setGuides(guides);
        return true;
      },
      render: (ctx, left, top) => {
        ctx.save();
        ctx.fillStyle = '#fff'; ctx.strokeStyle = '#26201a'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(left, top, 6, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.restore();
      },
    });
    line.controls = { p1: endpoint(true), p2: endpoint(false) };
  }

  /**
   * Build a border Group (concentric stroked rings / boxes) from a preset spec,
   * scaled to the active die. Shared by the toolbar Border button and template
   * loading. Returns null if the spec produces no parts.
   */
  #buildBorderGroup(spec) {
    if (!spec || !this.#area) return null;
    const a = this.#area;
    const parts = [];
    (spec.circles || []).forEach((c) => {
      const r = a.radius * c.r;
      parts.push(new Circle({ left: 0, top: 0, originX: 'center', originY: 'center', radius: r, fill: '', stroke: this.#ink.hex, strokeWidth: Math.max(1, a.radius * c.w), strokeUniform: true, strokeDashArray: c.dash || null }));
    });
    (spec.rects || []).forEach((rc) => {
      // inset is a fraction of a.w (same basis as strokeWidth below) applied as an absolute
      // pixel amount to BOTH axes, so the outer stroke edge lands flush with a.w AND a.h on a
      // non-square die -- a per-axis fraction (a.h * inset) would only be flush when a.w===a.h.
      const insetPx = a.w * rc.inset;
      const w = a.w - insetPx * 2, h = a.h - insetPx * 2;
      parts.push(new Rect({ left: 0, top: 0, originX: 'center', originY: 'center', width: w, height: h, rx: rc.radius ? a.w * rc.radius : 0, ry: rc.radius ? a.w * rc.radius : 0, fill: '', stroke: this.#ink.hex, strokeWidth: Math.max(1, a.w * rc.w), strokeUniform: true, strokeDashArray: rc.dash || null }));
    });
    if (!parts.length) return null;
    // Round borders are fully locked once placed -- no select/move/resize at all,
    // they're a fixed part of the die (per user request). Rectangle borders are
    // untouched: still selectable and free-scaling on all 8 handles.
    const g = new Group(parts, {
      left: a.cx, top: a.cy, originX: 'center', originY: 'center', sdType: 'border', sdBorderId: spec.id,
      perPixelTargetFind: true, sdUniform: this.#shape === 'round',
      selectable: this.#shape !== 'round', evented: this.#shape !== 'round',
    });
    g.sdDefaultW = g.width;   // snap-back targets for hand-resize (#onScaling)
    g.sdDefaultH = g.height;
    return g;
  }

  addBorder(e) {
    const borderId = e?.target?.closest?.('[data-border]')?.dataset.border || '';
    // round's default is the two-outer-rings + inner-ring seal frame (matches the
    // reference design); rectangle keeps its flush single box.
    const defaultId = this.#shape === 'round' ? 'ring-triple' : 'box-single';
    const spec = getBorder(borderId) || getBorder(defaultId) || SD_BORDERS.find((b) => b.shapes.includes(this.#shape));
    const g = this.#buildBorderGroup(spec);
    if (g) this.#addObject(g, this.#shape !== 'round');
  }

  // ----------------------------------------------------------- add: icons ---
  addIcon(e) {
    const iconId = e?.target?.closest?.('[data-icon]')?.dataset.icon;
    const icon = getIcon(iconId) || SD_ICONS[0];
    const p = new Path(icon.path, {
      left: this.#area.cx, top: this.#area.cy, originX: 'center', originY: 'center',
      fill: this.#ink.hex, sdType: 'icon', sdIconId: icon.id, sdUniform: true,
      perPixelTargetFind: this.#shape === 'round',
    });
    const target = Math.min(this.#area.w, this.#area.h) * 0.3;
    p.scaleToWidth(target);
    this.#addObject(p, true);
  }

  // ---------------------------------------------------------- add helper ---
  /**
   * Force an object to scale uniformly: hide the four mid-edge handles so only
   * the corners remain (corner scaling is uniform via canvas.uniformScaling),
   * which keeps logos/icons/curved text/plain text from being stretched out of
   * proportion. Enforced again mid-scale in #snapResize.
   */
  #lockUniform(obj) {
    if (!(obj && obj.setControlsVisibility)) return;
    // The date band is never the shopper's to move, on any device. It reaches
    // here because it is text like any other, and the movement rules below are
    // deliberately recomputed from scratch each time -- which would quietly
    // hand it back its freedom.
    if (obj.sdDateBand) return;
    const vis = { ml: false, mr: false, mt: false, mb: false };
    const isText = obj.sdType === 'text' || obj.sdType === 'curvedText';
    // sdNoResize (round plain text): no drag-to-resize at all, on any device --
    // only the Size field changes it (existing, unchanged). Every other text
    // case -- round curved text, and now ANY rectangle text -- gets the same
    // lock, but only while the CURRENT gesture's input is touch, decided
    // fresh here rather than baked into the object, so it tracks touch-vs-
    // mouse per interaction (mouse/desktop keeps corner handles everywhere
    // except round plain text's pre-existing all-device lock). Rotation
    // (mtr) is left alone either way, only asked to remove resizing.
    const touchTextLock = isText && this.#lastInputWasTouch;
    if (obj.sdNoResize || touchTextLock) { vis.tl = false; vis.tr = false; vis.bl = false; vis.br = false; }
    obj.setControlsVisibility(vis);
    // Movement: round text (plain + curved) becomes completely fixed on
    // touch -- not just unresizable, unmovable too -- matching how the round
    // border is already fully locked once placed. Round-only, not extended
    // to rectangle. Recomputed fresh every time for the same reason as the
    // resize lock above: an object switching device or shape mid-life (e.g.
    // undo restoring it, or a template reload) must not carry a stale lock.
    if (isText) {
      const moveLock = this.#shape === 'round' && this.#lastInputWasTouch;
      obj.lockMovementX = moveLock;
      obj.lockMovementY = moveLock;
    }
  }
  #reapplyUniform() {
    this.#userObjects().forEach((o) => { if (o.sdUniform) this.#lockUniform(o); });
  }
  #relockActive() {
    const o = this.#canvas.getActiveObject();
    if (o?.sdUniform) this.#lockUniform(o);
  }

  #addObject(obj, select) {
    obj.clipPath = this.#clipFor(obj);
    if (obj.sdUniform) this.#lockUniform(obj);
    this.#canvas.add(obj);
    this.#sinkGuides();
    if (select) { this.#canvas.setActiveObject(obj); }
    this.#canvas.requestRenderAll();
    if (!this.#loading) { this.#snapshot(); this.#dispatchChanged(); this.#syncInspector(); this.#scheduleEmit(); }
  }

  // -------------------------------------------------------------- events ---
  #bindCanvasEvents() {
    const c = this.#canvas;
    // Raw physical touch displacement, tracked independently of Fabric's own
    // click/drag bookkeeping. Verified this is necessary: opt.isClick on
    // mouse:up stays true for a real multi-step touch drag whenever
    // allowTouchScrolling lets the gesture through uncaptured (Fabric never
    // runs its own movement tracking in that case), so it can't be used to
    // tell a scroll-swipe apart from a stationary tap. This can.
    const upperCanvasEl = c.upperCanvasEl;
    if (upperCanvasEl) {
      upperCanvasEl.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        this.#touchStartX = t.clientX; this.#touchStartY = t.clientY; this.#touchMoved = false;
      }, { capture: true, passive: true });
      upperCanvasEl.addEventListener('touchmove', (e) => {
        const t = e.touches[0];
        if (Math.hypot(t.clientX - this.#touchStartX, t.clientY - this.#touchStartY) > 8) this.#touchMoved = true;
      }, { capture: true, passive: true });
    }
    // selection:created/updated fire on a FRESH tap-to-select, which
    // mouse:down:before below can't cover (it runs before the new selection
    // exists) -- both paths funnel through this so the touch-resize-lock is
    // correct immediately, not just from the interaction after this one.
    c.on('selection:created', () => { this.#relockActive(); this.#syncInspector(); });
    c.on('selection:updated', () => { this.#relockActive(); this.#syncInspector(); });
    c.on('selection:cleared', () => this.#syncInspector());
    c.on('object:modified', (e) => { this.#bakeCurvedScale(e && e.target); this.#snapshot(); this.#dispatchChanged(); this.#scheduleEmit(); });
    c.on('text:changed', () => { this.#dispatchChanged(); this.#scheduleEmit(); });
    c.on('object:moving', (e) => { this.#snapMove(e); this.#keepOffDateBand(e && e.target); });
    c.on('object:scaling', (e) => { this.#snapResize(e); this.#keepOffDateBand(e && e.target); });
    // Fires before Fabric reads canvas.selection for marquee-start and before
    // the active object changes -- the one correct place to decide, fresh for
    // every gesture, whether this is touch and what that should mean for the
    // about-to-happen resize-handle visibility and marquee-select behaviour.
    // (Covers pressing on an ALREADY-selected object's own handle, which
    // selection:created/updated above does not fire for -- selection isn't
    // changing in that case.)
    c.on('mouse:down:before', (opt) => {
      this.#lastInputWasTouch = isTouchLikeEvent(opt.e);
      this.#preDownActive = this.#canvas.getActiveObject();
      this.#relockActive();
      // Touch only, both shapes: no marquee/rubber-band multi-select -- an
      // empty-space drag means scroll now (allowTouchScrolling above), not
      // draw a selection box; the two would otherwise fight over the same
      // gesture. Leave #lineMode's own canvas.selection toggle
      // (startLine/#exitLineMode) completely alone -- it already owns this
      // flag while active.
      if (!this.#lineMode) {
        this.#canvas.selection = !this.#lastInputWasTouch;
      }
    });
    c.on('mouse:down', (e) => this.#onLineDown(e));
    c.on('mouse:move', (e) => this.#onLineMove(e));
    c.on('mouse:up', (opt) => {
      if (this.#lineMode) { this.#onLineUp(); return; }
      this.#setGuides([]);
      // A touch gesture that physically moved (a scroll-swipe reaching down
      // toward the inspector panel) shouldn't deselect whatever was active
      // before it started, even though Fabric's own mousedown handling
      // already discarded it -- restore it here. A genuine stationary tap
      // (touchMoved false) is left alone so tapping empty space still
      // deselects normally.
      if (this.#touchMoved && this.#preDownActive && !this.#canvas.getActiveObject()) {
        this.#canvas.setActiveObject(this.#preDownActive);
        this.#canvas.requestRenderAll();
      }
      // Curved text has no native inline-edit path (unlike plain IText, which
      // already enters editing when you click/tap its already-active self
      // again, via Fabric's own click-cycle tracking) -- so it's given the
      // same "tapped/clicked the already-active object again" trigger here.
      // opt.isClick is Fabric's own "no drag happened during this gesture" flag.
      if (opt.isClick && opt.target && opt.target === this.#preDownActive && opt.target.sdType === 'curvedText') {
        this.#openCurvedTextOverlay(opt.target);
      }
    });
  }

  // ---------------------------------------------------- keyboard shortcuts ---
  #handleKey(e) {
    if (!this.#canvas) return;
    const ae = document.activeElement;
    const inPanel = !!(ae && this.contains(ae) && /^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName));
    const active = this.#canvas.getActiveObject();
    const editing = !!(active && active.isEditing);

    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase();
      if (k === 'z') { e.preventDefault(); if (e.shiftKey) this.redo(); else this.undo(); return; }
      if (k === 'y') { e.preventDefault(); this.redo(); return; }
      if (editing || inPanel) return;   // let the browser copy/paste inside inputs
      if (k === 'd') { e.preventDefault(); this.duplicateActive(); return; }
      if (k === 'c') { e.preventDefault(); this.#copyActive(); return; }
      if (k === 'v') { e.preventDefault(); this.#pasteClipboard(); return; }
      return;
    }

    if (e.key === 'Escape') {
      if (!this.refs.fontPanel?.hidden || !this.refs.sizePanel?.hidden) { this.#closeAllCombos(); return; }
      if (this.#lineMode) { this.#exitLineMode(); return; }
      if (editing) active.exitEditing?.();
      else { this.#canvas.discardActiveObject(); this.#canvas.requestRenderAll(); this.#syncInspector(); }
      return;
    }

    if (editing || inPanel) return;     // never hijack typing / panel controls

    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (active && !active.sdGuide) { e.preventDefault(); this.deleteActive(); }
      return;
    }

    const dirs = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const dir = dirs[e.key];
    if (!dir || !active || active.sdGuide) return;
    e.preventDefault();
    const step = e.shiftKey ? 10 : 1;
    active.set({ left: (active.left || 0) + dir[0] * step, top: (active.top || 0) + dir[1] * step });
    active.setCoords();
    this.#canvas.requestRenderAll();
    this.#dispatchChanged();
    clearTimeout(this.#nudgeTimer);
    this.#nudgeTimer = setTimeout(() => { this.#snapshot(); this.#scheduleEmit(); }, 350);
  }

  async #copyActive() {
    const o = this.#canvas.getActiveObject();
    if (!o || o.sdGuide) return;
    this.#clipboard = await o.clone(EXTRA_PROPS);
  }
  async #pasteClipboard() {
    if (!this.#clipboard) return;
    const c = await this.#clipboard.clone(EXTRA_PROPS);
    c.set({ left: (c.left || 0) + 14, top: (c.top || 0) + 14 });
    this.#addObject(c, true);
  }

  // --------------------------------------------------------- inspector UI ---
  /**
   * Write a value into a ref'd form control, but NEVER while the user is
   * actively interacting with it. Assigning `.value` to a range input mid-drag
   * resets the native thumb's pointer tracking (the thumb sticks even though the
   * value changed) — the classic "slider won't move but its effect shows" bug.
   */
  #setFieldValue(refName, value) {
    const el = this.refs[refName];
    if (el && el !== document.activeElement) el.value = value;
  }

  /** Show controls appropriate to the active object + populate values. */
  /**
   * Round dies have no way to add a border except this button (templates bake
   * one in directly), so it stays available on a blank round design -- but
   * once a border exists it's permanent (already selectable:false/evented:
   * false once placed), so the button disappears rather than staying around
   * with nothing left for it to do. Rectangle is untouched, always shown.
   */
  #syncBorderButton() {
    const btn = this.refs.borderBtn;
    if (!btn) return;
    const hasBorder = this.#userObjects().some((o) => o.sdType === 'border');
    btn.hidden = this.#shape === 'round' && hasBorder;
  }

  #syncInspector() {
    this.#syncBorderButton();
    const obj = this.#canvas.getActiveObject();
    const r = this.refs;
    const type = obj && !obj.sdGuide ? obj.sdType : null;
    const show = (ref, on) => { if (r[ref]) r[ref].hidden = !on; };
    show('inspector', !!type);
    show('inspText', type === 'text' || type === 'curvedText');
    show('inspCurve', type === 'curvedText');
    show('inspImage', type === 'image');
    show('inspShape', type === 'shape' || type === 'border');
    show('objActions', !!type);
    // Curveness specifically (not the whole curved-text panel) is round-only fixed:
    // existing ring/arc text from a template still shows its text + arc side fields.
    if (r.curveField) r.curveField.hidden = this.#shape === 'round';
    if (!obj || !type) return;
    if (type === 'text' || type === 'curvedText') {
      const font = getFont(obj.fontId);
      if (r.fontTrigger) {
        r.fontTrigger.textContent = font.label;
        r.fontTrigger.style.fontFamily = `'${font.cssFamily}', ${font.fallback}`;
        r.fontTrigger.dataset.value = font.id;
      }
      this.#setFieldValue('sizeInput', Math.round((type === 'curvedText' ? obj.sdFontSize || obj.fontSize : obj.fontSize) || 34));
      if (r.boldBtn) {
        const hasBold = font.weights.includes(700);
        r.boldBtn.disabled = !hasBold;
        r.boldBtn.setAttribute('aria-pressed', String(hasBold && (obj.sdWeight || obj.fontWeight) >= 700));
      }
      const spacing = type === 'curvedText' ? (obj.sdSpacing || 0) : (obj.charSpacing || 0);
      this.#setFieldValue('spacingInput', Math.round(spacing / 10));
    }
    if (type === 'curvedText') {
      if (r.arcSelect) r.arcSelect.value = obj.sdArc || 'top';
      this.#setFieldValue('curveTextInput', obj.sdText || '');
      this.#setFieldValue('curveInput', this.#shape === 'rectangle'
        ? Math.round((1 - (obj.sdFlatten ?? 0.5)) * 100)
        : this.#radiusToCurve(obj.sdRadius || this.#curveBase()));
    }
    if (type === 'image') {
      this.#setFieldValue('thresholdInput', obj.sdThreshold ?? 128);
      if (r.modeSelect) r.modeSelect.value = obj.sdMode || 'threshold';
    }
    if (type === 'shape' || type === 'border') {
      this.#setFieldValue('strokeInput', Math.round(obj.strokeWidth || 3));
    }
  }

  // ------------------------------------------------------ text control in ---
  #applyFont(fontId) {
    const obj = this.#activeText(); if (!obj) return;
    const font = getFont(fontId);
    if (obj.sdType === 'curvedText') { obj.fontId = font.id; this.#rebuildCurved(obj); }
    else { obj.set({ fontFamily: font.cssFamily, fontId: font.id }); }
    if (this.refs.fontTrigger) {
      this.refs.fontTrigger.textContent = font.label;
      this.refs.fontTrigger.style.fontFamily = `'${font.cssFamily}', ${font.fallback}`;
      this.refs.fontTrigger.dataset.value = font.id;
    }
    this.#after();
  }

  // -------------------------------------------------- font/size dropdowns ---
  /**
   * Anchored popover lists for the Font and Size fields: opens just below its
   * trigger, capped to ~10 rows with scroll for the rest, and flips to open
   * upward when there isn't room below (native <select>/<datalist> gave no
   * control over this and the size one wasn't opening reliably at all).
   */
  openFontPanel() {
    const obj = this.#activeText();
    const current = obj ? obj.fontId : this.refs.fontTrigger?.dataset.value;
    const items = SD_FONTS.map((f) => ({ value: f.id, label: f.label, style: `font-family:'${f.cssFamily}',${f.fallback}` }));
    this.#openCombo('fontTrigger', 'fontPanel', items, current, (val) => this.#applyFont(val));
  }
  openSizePanel() {
    const obj = this.#activeText();
    const current = String(Math.round(obj ? (obj.sdType === 'curvedText' ? obj.sdFontSize || obj.fontSize : obj.fontSize) || 34 : this.refs.sizeInput?.value || 34));
    const presets = [6, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 64, 96];
    const items = presets.map((v) => ({ value: String(v), label: String(v) }));
    this.#openCombo('sizeInput', 'sizePanel', items, current, (val) => {
      if (this.refs.sizeInput) this.refs.sizeInput.value = val;
      this.onSizeChange({ target: this.refs.sizeInput });
    });
  }
  #openCombo(triggerRef, panelRef, items, currentValue, onPick) {
    const trigger = this.refs[triggerRef];
    const panel = this.refs[panelRef];
    if (!trigger || !panel) return;
    if (!panel.hidden) { this.#closeCombo(panelRef); return; }
    this.#closeAllCombos();
    // several fonts' fallback stacks contain literal double-quotes (e.g. "Arial
    // Narrow") which would otherwise break out of the style="..." attribute.
    const escAttr = (s) => String(s).replace(/"/g, '&quot;');
    panel.innerHTML = items.map((it) =>
      `<button type="button" class="sd__combo-item${it.value === currentValue ? ' is-active' : ''}" data-value="${it.value}"${it.style ? ` style="${escAttr(it.style)}"` : ''}>${it.label}</button>`
    ).join('');
    panel.hidden = false;
    trigger.setAttribute?.('aria-expanded', 'true');
    this.#positionCombo(trigger, panel);
    const onPickClick = (ev) => {
      const btn = ev.target.closest('[data-value]'); if (!btn) return;
      onPick(btn.dataset.value);
      this.#closeCombo(panelRef);
    };
    const onOutside = (ev) => { if (!panel.contains(ev.target) && ev.target !== trigger) this.#closeCombo(panelRef); };
    panel.addEventListener('click', onPickClick);
    panel.__sdOutside = onOutside;
    panel.__sdTrigger = trigger;
    setTimeout(() => { document.addEventListener('mousedown', onOutside); document.addEventListener('touchstart', onOutside); }, 0);
  }
  #closeCombo(panelRef) {
    const panel = this.refs[panelRef];
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    if (panel.__sdOutside) {
      document.removeEventListener('mousedown', panel.__sdOutside);
      document.removeEventListener('touchstart', panel.__sdOutside);
      panel.__sdOutside = null;
    }
    panel.__sdTrigger?.setAttribute?.('aria-expanded', 'false');
  }
  #closeAllCombos() { this.#closeCombo('fontPanel'); this.#closeCombo('sizePanel'); }
  #positionCombo(trigger, panel) {
    const r = trigger.getBoundingClientRect();
    panel.style.width = `${Math.max(r.width, 170)}px`;
    panel.style.maxHeight = '328px';   // ~10 rows; the rest scrolls
    const needed = Math.min(328, panel.scrollHeight || 328);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < needed + 8;
    panel.style.top = openUp ? `${Math.max(8, r.top - needed - 4)}px` : `${r.bottom + 4}px`;
    panel.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - r.width - 8))}px`;
  }

  // ---------------------------------------------- curved-text inline edit ---
  // Curved text has no Fabric-native editable cursor (its "glyphs" are
  // individually positioned, non-interactive characters, not one IText) --
  // this floats a plain input over the object's on-screen box instead,
  // live-rebuilding the curve through the same #rebuildCurved path the
  // sidebar's own curveTextInput already uses.
  #curvedTextScreenBox(obj) {
    const el = this.refs.canvas;
    if (!el) return null;
    obj.setCoords();
    const bb = obj.getBoundingRect();
    const rect = el.getBoundingClientRect();
    const scale = (el.clientWidth || CANVAS) / CANVAS;   // same convention as #positionDimLabels
    return {
      left: rect.left + bb.left * scale, top: rect.top + bb.top * scale,
      width: bb.width * scale, height: bb.height * scale,
    };
  }
  #positionCurvedOverlay(box) {
    const input = this.refs.curveOverlayInput;
    if (!input || !box) return;
    const w = Math.max(140, box.width);
    let left = box.left + box.width / 2 - w / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    let top = box.top - 44;                            // prefer just above the object
    if (top < 8) top = box.top + box.height + 8;        // flip below if no room above
    top = Math.max(8, Math.min(top, window.innerHeight - 48));
    input.style.left = `${left}px`;
    input.style.top = `${top}px`;
    input.style.width = `${w}px`;
  }
  #openCurvedTextOverlay(obj) {
    if (this.#curvedEditTarget) this.#closeCurvedTextOverlay(true);
    const input = this.refs.curveOverlayInput;
    const box = this.#curvedTextScreenBox(obj);
    if (!input || !box) return;
    this.#curvedEditTarget = obj;
    input.value = obj.sdText || '';
    input.hidden = false;
    this.#positionCurvedOverlay(box);

    const onInput = () => {
      const o = this.#curvedEditTarget;
      if (!o) return;
      o.sdText = input.value;
      this.#rebuildCurved(o);   // swaps in a FRESH Group -- must re-track it below
      this.#curvedEditTarget = this.#canvas.getActiveObject();
      this.#positionCurvedOverlay(this.#curvedTextScreenBox(this.#curvedEditTarget));
      this.#dispatchChanged();
    };
    const commit = () => this.#closeCurvedTextOverlay(true);
    const onKey = (e) => {
      // Escape commits rather than reverts, matching this editor's existing
      // text fields (none of them have an explicit cancel-on-Escape either).
      if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); commit(); }
    };
    const onOutside = (ev) => { if (ev.target !== input) this.#closeCurvedTextOverlay(true); };

    input.addEventListener('input', onInput);
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', onKey);
    setTimeout(() => { document.addEventListener('mousedown', onOutside); document.addEventListener('touchstart', onOutside); }, 0);
    this.#curvedEditCleanup = () => {
      input.removeEventListener('input', onInput);
      input.removeEventListener('blur', commit);
      input.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
    requestAnimationFrame(() => { input.focus(); input.select(); });
  }
  #closeCurvedTextOverlay(commit) {
    const input = this.refs.curveOverlayInput;
    if (!input || !this.#curvedEditTarget) return;
    this.#curvedEditCleanup?.();
    this.#curvedEditCleanup = null;
    input.hidden = true;
    this.#curvedEditTarget = null;
    if (commit) { this.#snapshot(); this.#dispatchChanged(); this.#scheduleEmit(); this.#syncInspector(); }
  }

  onSizeChange(e) {
    const obj = this.#activeText(); if (!obj) return;
    const px = Math.max(6, Math.min(160, parseInt(e.target.value, 10) || 34));
    if (obj.sdType === 'curvedText') { obj.sdFontSize = px; this.#rebuildCurved(obj); }
    else { obj.set('fontSize', px); }
    this.#after();
  }
  toggleBold() {
    const obj = this.#activeText(); if (!obj) return;
    const font = getFont(obj.fontId);
    if (!font.weights.includes(700)) return;      // no real bold face vendored
    const bold = (obj.sdWeight || obj.fontWeight) >= 700 ? 400 : 700;
    obj.sdWeight = bold;
    if (obj.sdType === 'curvedText') this.#rebuildCurved(obj);
    else obj.set('fontWeight', bold);
    this.refs.boldBtn?.setAttribute('aria-pressed', String(bold >= 700));
    this.#after();
  }
  onSpacingChange(e) {
    const obj = this.#activeText(); if (!obj) return;
    const cs = (parseInt(e.target.value, 10) || 0) * 10;   // UI 0..40 -> fabric 1/1000em
    if (obj.sdType === 'curvedText') { obj.sdSpacing = cs; this.#rebuildCurved(obj); }
    else obj.set('charSpacing', cs);
    this.#after();
  }
  /** Position the selected element to the left edge / centre / right edge of the die. */
  setAlign(e) {
    const obj = this.#activeText(); if (!obj) return;
    const dir = e.target.closest('[data-align]')?.dataset.align || 'center';
    const a = this.#area;
    const half = obj.getScaledWidth() / 2;
    const cx = dir === 'left' ? a.cx - a.w / 2 + half
      : dir === 'right' ? a.cx + a.w / 2 - half
        : a.cx;
    obj.set('left', cx);       // originX is 'center'
    obj.setCoords();
    this.#after();
  }
  onCurveChange(e) {
    const obj = this.#activeText(); if (!obj || obj.sdType !== 'curvedText') return;
    const parsed = parseInt(e.target.value, 10);
    const v = Math.max(0, Math.min(100, Number.isFinite(parsed) ? parsed : 50));   // 0 is valid
    if (this.#shape === 'rectangle') {
      // Rectangle: the slider is a single flatten<->circular dial (0 = flat,
      // 100 = completely circular, at the same tight radius the old slider's
      // 100 already used) -- curvature is purely the flatten blend now, so
      // sdRadius stays fixed rather than also varying.
      obj.sdRadius = this.#curveRange().min;
      obj.sdFlatten = 1 - v / 100;
    } else {
      obj.sdRadius = this.#curveToRadius(v);
    }
    this.#rebuildCurved(obj);
    this.#after();
  }
  // Round: curveness slider (0 gentlest .. 100 tightest) <-> arc radius. The
  // range is clamped to the die so the text can never drift off-canvas: at
  // the gentle end it hugs just inside the edge; at the tight end it forms a
  // small inner ring. Rectangle no longer uses this radius mapping directly
  // (see onCurveChange/#buildCurvedGroup's sdFlatten) but #curveRange().min
  // is still reused there as the fixed "completely circular" reference.
  #curveBase() { return this.#shape === 'round' ? this.#area.radius : Math.min(this.#area.w, this.#area.h) / 2; }
  #curveRange() { const b = this.#curveBase(); return { max: b * 0.9, min: b * 0.48 }; }
  #curveToRadius(v) { const { max, min } = this.#curveRange(); const c = Math.max(0, Math.min(100, v)); return max - (c / 100) * (max - min); }
  #radiusToCurve(radius) {
    const { max, min } = this.#curveRange();
    return Math.max(0, Math.min(100, Math.round(((max - radius) / (max - min || 1)) * 100)));
  }
  onArcChange(e) {
    const obj = this.#activeText(); if (!obj || obj.sdType !== 'curvedText') return;
    obj.sdArc = e.target.value; this.#rebuildCurved(obj); this.#after();
  }

  #activeText() {
    const o = this.#canvas.getActiveObject();
    return o && (o.sdType === 'text' || o.sdType === 'curvedText') ? o : null;
  }

  // ---------------------------------------------------------- shape control ---
  onStrokeChange(e) {
    const obj = this.#canvas.getActiveObject(); if (!obj) return;
    const w = Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 3));
    if (obj.sdType === 'border' && obj._objects) obj._objects.forEach((p) => p.set('strokeWidth', w));
    else obj.set('strokeWidth', w);
    obj.dirty = true; this.#after();
  }

  // ----------------------------------------------------------------- ink ---
  #applyInkSwatches() {
    (this.querySelectorAll('[data-ink]') || []).forEach((el) => {
      el.style.setProperty('--sw', getInk(el.dataset.ink).hex);
      el.classList.toggle('is-selected', el.dataset.ink === this.#ink.name);
    });
  }

  /** Populate the font picker + symbol palette from the shared catalogue. */
  #buildControls() {
    const r = this.refs;
    // Font choices are rendered on demand by openFontPanel (each item previewed
    // in its own typeface) -- fontTrigger's label is set by #syncInspector/#applyFont.
    if (r.symbolPalette && !r.symbolPalette.children.length) {
      r.symbolPalette.innerHTML = SD_ICONS
        .map((ic) => `<button type="button" class="sd__symbol" data-icon="${ic.id}" on:click="/addIcon" title="${ic.label}" aria-label="${ic.label}"><svg viewBox="0 0 100 100" width="24" height="24"><path d="${ic.path}" fill="currentColor"/></svg></button>`)
        .join('');
    }
  }

  toggleSymbols() {
    if (this.refs.symbolPalette) this.refs.symbolPalette.hidden = !this.refs.symbolPalette.hidden;
  }
  setInk(e) {
    const name = e.target.closest('[data-ink]')?.dataset.ink; if (!name) return;
    this.#ink = getInk(name);
    (this.querySelectorAll('[data-ink]') || []).forEach((el) => el.classList.toggle('is-selected', el.dataset.ink === name));
    this.#recolor();
    this.#after();
  }
  #recolor() {
    this.#userObjects().forEach((o) => this.#recolorObject(o));
    this.#canvas.requestRenderAll();
  }
  #recolorObject(o) {
    const hex = this.#ink.hex;
    if (o.sdType === 'image') { this.#regenImage(o); return; }
    if (o.sdType === 'border' && o._objects) { o._objects.forEach((p) => p.set('stroke', hex)); o.dirty = true; return; }
    if (o.sdType === 'curvedText' && o._objects) { o._objects.forEach((p) => { if (!p.sdCenter && p.set) p.set('fill', hex); }); o.dirty = true; return; }
    if (o.sdType === 'shape') { o.set(o.stroke ? 'stroke' : 'fill', hex); return; }
    if ('fill' in o) o.set('fill', hex);
  }

  // -------------------------------------------------------- object actions ---
  deleteActive() {
    const objs = this.#canvas.getActiveObjects().filter((o) => !o.sdGuide);
    if (!objs.length) return;
    objs.forEach((o) => this.#canvas.remove(o));
    this.#canvas.discardActiveObject();
    this.#after(true);
  }
  async duplicateActive() {
    const obj = this.#canvas.getActiveObject(); if (!obj || obj.sdGuide) return;
    const cloned = await obj.clone(EXTRA_PROPS);
    cloned.set({ left: (obj.left || 0) + 16, top: (obj.top || 0) + 16 });
    this.#addObject(cloned, true);
  }

  // ------------------------------------------------------------- undo/redo ---
  #snapshot() {
    if (this.#loading) return;
    const json = JSON.stringify(this.#canvas.toObject(EXTRA_PROPS));
    if (this.#undo.length && this.#undo[this.#undo.length - 1] === json) return;
    this.#undo.push(json);
    if (this.#undo.length > MAX_UNDO) this.#undo.shift();
    this.#redo.length = 0;
    this.#syncHistoryButtons();
  }
  undo() {
    if (this.#undo.length < 2) return;
    this.#redo.push(this.#undo.pop());
    this.#restore(this.#undo[this.#undo.length - 1]);
  }
  redo() {
    if (!this.#redo.length) return;
    const json = this.#redo.pop(); this.#undo.push(json); this.#restore(json);
  }
  async #restore(json) {
    this.#loading = true;
    await this.#canvas.loadFromJSON(JSON.parse(json));
    this.#buildGuides();
    this.#reclipAll();
    this.#reapplyUniform();
    this.#userObjects().forEach((o) => { if ((o.type || '').toLowerCase() === 'line') this.#applyLineControls(o); });
    this.#canvas.requestRenderAll();
    this.#loading = false;
    this.#syncHistoryButtons();
    this.#syncInspector();
    this.#dispatchChanged();
    this.#scheduleEmit();
  }
  #syncHistoryButtons() {
    if (this.refs.undoBtn) this.refs.undoBtn.disabled = this.#undo.length < 2;
    if (this.refs.redoBtn) this.refs.redoBtn.disabled = this.#redo.length === 0;
  }

  // ----------------------------------------------------------------- zoom ---
  zoomIn() { this.#zoom(1.15); }
  zoomOut() { this.#zoom(1 / 1.15); }
  zoomReset() { this.#canvas.setZoom(1); this.#canvas.setViewportTransform([1, 0, 0, 1, 0, 0]); this.#canvas.requestRenderAll(); }
  #zoom(f) {
    const z = Math.max(0.5, Math.min(3, this.#canvas.getZoom() * f));
    this.#canvas.zoomToPoint({ x: CENTER, y: CENTER }, z);
    this.#canvas.requestRenderAll();
  }

  // ============================================================== snapping ===
  /** Snap x/y target lines: die centre + edges + safe margin; figma adds other elements. */
  #snapTargets(exclude) {
    const a = this.#area; const xs = []; const ys = [];
    if (a) {
      const halfW = this.#shape === 'round' ? a.radius : a.w / 2;
      const halfH = this.#shape === 'round' ? a.radius : a.h / 2;
      const safeW = halfW * (1 - SAFE_INSET * 2);
      const safeH = halfH * (1 - SAFE_INSET * 2);
      xs.push(a.cx, a.cx - halfW, a.cx + halfW, a.cx - safeW, a.cx + safeW);
      ys.push(a.cy, a.cy - halfH, a.cy + halfH, a.cy - safeH, a.cy + safeH);
    }
    if (this.#snapLevel === 'figma') {
      this.#userObjects().forEach((o) => {
        if (o === exclude) return;
        const bb = o.getBoundingRect();
        xs.push(bb.left, bb.left + bb.width / 2, bb.left + bb.width);
        ys.push(bb.top, bb.top + bb.height / 2, bb.top + bb.height);
      });
    }
    return { xs, ys };
  }

  #nearest(value, targets, tol) {
    let best = null;
    for (const t of targets) {
      const d = Math.abs(value - t);
      if (d <= tol && (best === null || d < best.d)) best = { d, pos: t };
    }
    return best;
  }

  #snapMove(e) {
    const o = e.target; if (!o || o.sdGuide) return;
    if (e.e && e.e.altKey) { this.#setGuides([]); return; }   // Alt bypasses snapping
    const tol = 6;
    o.setCoords();                                            // fresh bbox for the current drag frame
    const b = o.getBoundingRect();
    const { xs, ys } = this.#snapTargets(o);
    const guides = [];
    const bestEdge = (edges, targets) => {
      let best = null;
      for (const ev of edges) { const n = this.#nearest(ev, targets, tol); if (n && (!best || n.d < best.d)) best = { edge: ev, pos: n.pos, d: n.d }; }
      return best;
    };
    const bx = bestEdge([b.left, b.left + b.width / 2, b.left + b.width], xs);
    if (bx) { o.left += bx.pos - bx.edge; guides.push({ axis: 'v', pos: bx.pos }); }
    const by = bestEdge([b.top, b.top + b.height / 2, b.top + b.height], ys);
    if (by) { o.top += by.pos - by.edge; guides.push({ axis: 'h', pos: by.pos }); }
    o.setCoords();
    this.#setGuides(guides);
  }

  #snapResize(e) {
    const o = e.target; if (!o || o.sdGuide) return;
    // uniform types (text, curved text, logos, icons, round borders) keep aspect ratio while scaling
    if (o.sdUniform) { o.scaleY = o.scaleX; o.setCoords(); return; }
    // resize-snap only for shapes/borders (single-axis snap would distort text/images/icons)
    if (!(o.sdType === 'border' || o.sdType === 'shape')) return;
    if (e.e && e.e.altKey) { this.#setGuides([]); return; }
    const t = e.transform || {};
    // A corner handle sets BOTH originX and originY; an edge-midpoint handle sets
    // only one. Guide-snapping only makes sense (and is only safe) on a single axis
    // at a time -- doing it independently on both axes during a corner drag decouples
    // scaleX from scaleY right after Fabric's own uniform corner-scaling had coupled
    // them, which is exactly what made a corner-dragged circle/box "snap, then
    // deform" as the drag continued. Skip snapping entirely on corner drags and let
    // Fabric's native uniform corner-scale stand; edge-handle (single-axis) drags are
    // unaffected and keep snapping exactly as before.
    const corner = (t.originX === 'left' || t.originX === 'right') && (t.originY === 'top' || t.originY === 'bottom');
    if (corner) { this.#setGuides([]); return; }
    const tol = 6;
    const guides = [];
    const { xs, ys } = this.#snapTargets(o);
    o.setCoords();
    let b = o.getBoundingRect();
    // X: the moving edge is opposite the anchor origin
    let movingX = t.originX === 'left' ? b.left + b.width : t.originX === 'right' ? b.left : null;
    let fixedX = t.originX === 'left' ? b.left : t.originX === 'right' ? b.left + b.width : null;
    if (movingX !== null) {
      const snap = this.#nearest(movingX, xs, tol);
      if (snap && b.width > 1) {
        o.scaleX *= Math.abs(snap.pos - fixedX) / b.width; o.setCoords();
        const nb = o.getBoundingRect();
        o.left += t.originX === 'left' ? fixedX - nb.left : fixedX - (nb.left + nb.width);
        o.setCoords();
        guides.push({ axis: 'v', pos: snap.pos });
      }
    }
    b = o.getBoundingRect();
    let movingY = t.originY === 'top' ? b.top + b.height : t.originY === 'bottom' ? b.top : null;
    let fixedY = t.originY === 'top' ? b.top : t.originY === 'bottom' ? b.top + b.height : null;
    if (movingY !== null) {
      const snap = this.#nearest(movingY, ys, tol);
      if (snap && b.height > 1) {
        o.scaleY *= Math.abs(snap.pos - fixedY) / b.height; o.setCoords();
        const nb = o.getBoundingRect();
        o.top += t.originY === 'top' ? fixedY - nb.top : fixedY - (nb.top + nb.height);
        o.setCoords();
        guides.push({ axis: 'h', pos: snap.pos });
      }
    }
    this.#setGuides(guides);
  }

  #ensureGuides() {
    if (this.#vGuide && this.#canvas.getObjects().includes(this.#vGuide)) return;
    const mk = (coords) => {
      const l = new Line(coords, { stroke: '#c0202a', strokeWidth: 1, selectable: false, evented: false, excludeFromExport: true, sdGuide: true, visible: false, hoverCursor: 'default' });
      this.#canvas.add(l); return l;
    };
    this.#vGuide = mk([CENTER, 0, CENTER, CANVAS]);
    this.#hGuide = mk([0, CENTER, CANVAS, CENTER]);
  }

  #setGuides(list) {
    if (!this.#canvas) return;
    this.#ensureGuides();
    const v = list.find((g) => g.axis === 'v');
    const h = list.find((g) => g.axis === 'h');
    if (v) { this.#vGuide.set({ x1: v.pos, x2: v.pos, y1: 0, y2: CANVAS, visible: true }); this.#canvas.bringObjectToFront?.(this.#vGuide); }
    else this.#vGuide.set('visible', false);
    if (h) { this.#hGuide.set({ x1: 0, x2: CANVAS, y1: h.pos, y2: h.pos, visible: true }); this.#canvas.bringObjectToFront?.(this.#hGuide); }
    else this.#hGuide.set('visible', false);
    this.#canvas.requestRenderAll();
  }

  // ------------------------------------------------------------- after-edit ---
  #after(snapshot) {
    this.#canvas.requestRenderAll();
    if (snapshot) this.#snapshot();
    this.#dispatchChanged();
    this.#syncInspector();
    this.#scheduleEmit();
  }

  // ---------------------------------------------------- design-changed evt ---
  #textLength() {
    return this.#userObjects().reduce((n, o) => {
      if (o.sdType === 'text') return n + (o.text || '').replace(/\s/g, '').length;
      if (o.sdType === 'curvedText') return n + (o.sdText || '').replace(/\s/g, '').length;
      return n;
    }, 0);
  }
  #dispatchChanged() {
    this.dispatchEvent(new CustomEvent('stamp:design-changed', { bubbles: true, detail: { textLength: this.#textLength() } }));
  }

  // ---------------------------------------------------------- launch params ---
  #applyLaunchParams() {
    const params = new URLSearchParams(location.search);
    const tid = params.get('template');
    if (tid) {
      const tpl = getTemplate(tid);
      if (tpl) {
        this.#pendingScene = templateToScene(tpl);
        // Only let the template dictate the shape when nothing has told us the
        // real die yet. This runs after `await #preloadFonts()`, so the studio
        // has usually already called setStampSize() with the true physical size
        // -- and re-shaping from the template would throw that away: the label
        // and `mm` are absent here, which forced round seals onto rectangular
        // dies and blanked the on-canvas size labels for every product that
        // arrives carrying a `stamp-size:` tag.
        //
        // When a real size is already set we leave it alone and just place the
        // scene. #pendingScene stays assigned either way, so a size chosen
        // later still re-places the design at the true aspect ratio.
        if (!this.#size || !this.#size.label) {
          this.setStampSize({ shape: this.#pendingScene.shape, label: '' });
        }
        this.#applyTemplateScene(this.#pendingScene);
      }
    } else if (params.get('mode') === 'upload') {
      // Start blank; setStampSize() shows the upload guidelines/welcome card
      // the first time a size is picked, in place of the bare canvas+toolbar.
      this.#pendingUploadIntro = true;
    }
  }

  /** Clear the canvas and place a starter template's semantic elements. */
  #applyTemplateScene(scene) {
    if (!scene || !this.#canvas) return;
    this.#loading = true;
    this.#userObjects().forEach((o) => this.#canvas.remove(o));
    this.#ink = getInk(scene.ink);
    this.#applyInkSwatches();
    const a = this.#area;
    const R = this.#shape === 'round' ? a.radius : Math.min(a.w, a.h) / 2;
    const ringLimitDeg = this.#ringSpanLimitDeg(scene, R);
    (scene.elements || []).forEach((el) => this.#placeTemplateEl(el, scene.fontId, a, R, ringLimitDeg));
    this.#reclipAll();
    this.#reapplyUniform();
    this.#loading = false;
    this.#sinkGuides();
    this.#syncBorderButton();
    this.#canvas.requestRenderAll();
    this.#snapshot();
    this.#dispatchChanged();
  }

  /**
   * Radius, in canvas px, of the gap between the round border's middle ring
   * (92.5%) and inner ring (58%) -- where ring/arc text and the star icon(s)
   * are centred, for both the single- and two-star layouts. Derived from
   * ring-triple's own radii rather than hardcoded, so it stays correct if
   * that preset ever changes.
   */
  #ringGapRadius(R) {
    const ring = getBorder('ring-triple');
    const c = ring && ring.circles;
    return c && c.length >= 3 ? R * (c[1].r + c[2].r) / 2 : R * 0.75;
  }

  /**
   * How far a ring/arc's ink may reach before something gets in its way,
   * outward and inward, in canvas px from the die centre.
   *
   * The die edge is the wrong limit, which is what made arc text look cut off.
   * The text sits in the band between two circles of the border, so a letter
   * that grows past the circle beside it is sliced by that line long before it
   * reaches the die -- measured directly: on a 38mm die the band's outer edge
   * is at 206 while the old clamp allowed 220, so enlarging the top arc ran the
   * capitals straight through the ring. Only a die with no border falls back to
   * the die itself.
   *
   * @param {number} radius Where the arc's glyph centres sit.
   * @returns {{ outer: number, inner: number }}
   */
  #arcBandLimits(radius) {
    const CLEARANCE = 3;
    const die = this.#area ? this.#area.radius : AREA / 2;
    const border = this.#userObjects().find((o) => o.sdType === 'border');
    const spec = border ? getBorder(border.sdBorderId) : null;
    let outer = die;
    let inner = 0;
    ((spec && spec.circles) || []).forEach((c) => {
      const half = die * (c.w || 0) / 2;
      const edgeIn = die * c.r - half;    // this circle's inner edge
      const edgeOut = die * c.r + half;   // ...and its outer edge
      if (edgeIn > radius && edgeIn < outer) outer = edgeIn;
      if (edgeOut < radius && edgeOut > inner) inner = edgeOut;
    });
    return { outer: outer - CLEARANCE, inner: inner + CLEARANCE };
  }

  /**
   * Safe angular budget (degrees) for a ring/arc text element, leaving real
   * clearance for any star icon(s) sharing the same ring gap -- otherwise a
   * long enough string visually runs into/behind them (confirmed: "Chartered
   * Accountants" on the r2-cpa template clipped at the naive R*0.162 size).
   * Two-star layouts split the circle in half at the stars (top/bottom arcs,
   * stars at 90/270); single-star layouts only have one star to clear, so the
   * ring text can use nearly the whole circle.
   */
  #ringSpanLimitDeg(scene, R) {
    const stars = (scene.elements || []).filter((el) => el.kind === 'star');
    if (!stars.length) return 350;
    const starWidthDeg = ((R * 0.22) / this.#ringGapRadius(R)) * (180 / Math.PI);
    const clearance = starWidthDeg * 1.6;   // beyond the star's own footprint
    return stars.length >= 2 ? Math.max(60, 180 - clearance) : Math.max(60, 360 - clearance);
  }

  /** The font size (<= base) that keeps `text`'s arc span within maxSpanDeg,
   *  measured via the same layout math #buildCurvedGroup uses -- scales down
   *  proportionally rather than a fixed cap, so most templates (which have
   *  plenty of headroom) keep the full bigger size and only the tight ones
   *  shrink just enough to actually fit. */
  #fitRingFontSize(text, fontId, baseSize, radius, maxSpanDeg) {
    const glyphs = this.#curvedLayout({ sdText: text, fontId, sdWeight: 400, sdFontSize: baseSize, sdSpacing: 0, sdArc: 'top', sdRadius: radius });
    if (!glyphs.length) return baseSize;
    const degs = glyphs.map((g) => g.deg);
    const span = Math.max(...degs) - Math.min(...degs);
    if (span <= maxSpanDeg || span <= 0) return baseSize;
    return Math.max(12, Math.round(baseSize * (maxSpanDeg / span)));
  }

  /** The font size (<= baseSize) that keeps a straight (non-curved) line of
   *  `text` within maxWidth, measured the same way the canvas itself will
   *  render it. Scales down proportionally rather than a fixed cap, so most
   *  template text keeps the full flat default and only genuinely tight
   *  strings/dies shrink to actually fit. */
  #fitStraightFontSize(text, fontId, weight, baseSize, maxWidth) {
    const font = getFont(fontId);
    const ctx = this.#measureCtx();
    ctx.font = `${weight} ${baseSize}px '${font.cssFamily}'`;
    const width = ctx.measureText(text || '').width;
    if (width <= maxWidth || width <= 0) return baseSize;
    return Math.max(11, Math.round(baseSize * (maxWidth / width)));
  }

  /** The font size (<= requestedSize) that keeps a ring/arc's reach inside the
   *  band it sits in -- see #arcBandLimits for why that is the border circle
   *  beside it and not the die.
   *
   *  Which way the ink reaches depends on the arc: a top arc's capitals grow
   *  outward, but a bottom arc's glyphs are rotated 180 degrees, so theirs grow
   *  toward the centre and it is the inner circle they run into. The old clamp
   *  measured both as `radius + ascent`, so a bottom arc was being checked
   *  against a limit in the wrong direction entirely.
   *
   *  The radius is a fixed offset, not something that scales with font size, so
   *  reach(size) = radius +/- size*ascentRatio is affine, not linear through the
   *  origin -- naively scaling the whole requested size by limit/reach
   *  undershoots the target (verified directly: it left reach well over budget
   *  after "shrinking"). Solving the affine equation for the size that actually
   *  lands reach on the limit is correct in one shot, still no iteration. */
  #fitRingRadialSize(text, fontId, weight, requestedSize, radius, arc) {
    const font = getFont(fontId);
    const ctx = this.#measureCtx();
    ctx.font = `${weight} ${requestedSize}px '${font.cssFamily}'`;
    const ascent = ctx.measureText(text || '').actualBoundingBoxAscent || requestedSize * 0.8;
    if (ascent <= 0) return requestedSize;

    const band = this.#arcBandLimits(radius);
    const available = arc === 'bottom' ? radius - band.inner : band.outer - radius;
    // A radius already outside its band leaves nothing to fit into; shrinking
    // to the 11px floor would just hide the problem somewhere off the die.
    if (available <= 0) return requestedSize;
    if (ascent <= available) return requestedSize;

    const ascentRatio = ascent / requestedSize;
    return Math.max(11, Math.floor(Math.min(requestedSize, available / ascentRatio)));
  }

  #placeTemplateEl(el, fontId, a, R, ringLimitDeg) {
    const font = getFont(fontId);
    const ink = this.#ink.hex;
    if (el.kind === 'ring') {
      const ringRadius = this.#ringGapRadius(R);
      const base = Math.max(12, Math.round(R * 0.162));
      const sdFontSize = this.#fitRingFontSize(el.text, fontId, base, ringRadius, ringLimitDeg ?? 350);
      const g = this.#buildCurvedGroup({ sdText: el.text, fontId, sdWeight: 400, sdFontSize, sdArc: el.position, sdRadius: ringRadius });
      g.set({ left: a.cx, top: a.cy });
      this.#canvas.add(g);
    } else if (el.kind === 'center') {
      // Chord width of the inner border circle at the text block's own
      // height (estimated from the flat base size), not the full diameter --
      // a single-line text block centred vertically doesn't get the full
      // width of the circle unless it has zero height.
      const innerR = R * 0.58;
      const estH = TEXT_BASE_SIZE * 1.2;
      const chord = 2 * Math.sqrt(Math.max(0, innerR * innerR - (estH / 2) * (estH / 2))) * 0.94;
      const fontSize = this.#fitStraightFontSize(el.text, fontId, 400, TEXT_BASE_SIZE, chord);
      this.#canvas.add(new IText(el.text, { left: a.cx, top: a.cy, originX: 'center', originY: 'center', fontFamily: font.cssFamily, fontSize, fill: ink, textAlign: 'center', sdType: 'text', fontId, sdWeight: 400, sdUniform: true, sdNoResize: this.#shape === 'round', perPixelTargetFind: this.#shape === 'round' }));
    } else if (el.kind === 'star') {
      const p = new Path(getIcon('star').path, { originX: 'center', originY: 'center', fill: ink, sdType: 'icon', sdIconId: 'star', sdUniform: true, perPixelTargetFind: this.#shape === 'round' });
      p.scaleToWidth(R * 0.22);
      // Single- and two-star layouts alike centre the star(s) in the same ring
      // gap the arc text sits in.
      const off = this.#ringGapRadius(R);
      const pos = el.position === 'left' ? { x: a.cx - off, y: a.cy }
        : el.position === 'right' ? { x: a.cx + off, y: a.cy }
          : { x: a.cx, y: a.cy + off };
      p.set({ left: pos.x, top: pos.y });
      this.#canvas.add(p);
    } else if (el.kind === 'line') {
      const n = el.total || 1;
      const gap = a.h / (n + 1);
      // Flat base, but never bigger than what its own vertical slot can hold
      // (more lines sharing the box = less room each) before also checking width.
      const base = Math.min(TEXT_BASE_SIZE, Math.max(11, Math.round(gap * 0.62)));
      const size = this.#fitStraightFontSize(el.text, fontId, 400, base, a.w * 0.88);
      const y = a.cy + (el.index - (n - 1) / 2) * gap;
      this.#canvas.add(new IText(el.text, { left: a.cx, top: y, originX: 'center', originY: 'center', fontFamily: font.cssFamily, fontSize: size, fill: ink, textAlign: 'center', sdType: 'text', fontId, sdWeight: 400, sdUniform: true }));
    } else if (el.kind === 'frame') {
      // Concentric ring/box border straight from a preset — the seal frame the
      // gallery thumbnail draws. Borders aren't die-clipped and export as-is.
      const g = this.#buildBorderGroup(getBorder(el.borderId));
      if (g) this.#canvas.add(g);
    } else if (el.kind === 'rule') {
      // A real horizontal drawn line (endpoint-editable like the line tool).
      // `yFrac`/`widthFrac` are optional: omit both (as the "for" designs do)
      // for the original centred 76%-width rule, or pass them to place a rule
      // anywhere down the die.
      const w = a.w * (el.widthFrac || 0.76);
      const y = el.yFrac != null ? a.cy - a.h / 2 + el.yFrac * a.h : a.cy;
      const line = new Line([a.cx - w / 2, y, a.cx + w / 2, y], {
        stroke: ink, strokeWidth: Math.max(1, Math.round(Math.min(a.w, a.h) * 0.006)), sdType: 'shape', strokeUniform: true,
      });
      this.#applyLineControls(line);
      this.#canvas.add(line);
    } else if (el.kind === 'dateBand') {
      // The rotating wheels, drawn so the shopper can see the impression -- not
      // a text element they own. It is pinned to the centre of the die, can't
      // be selected, moved, resized or typed over, and #keepOffDateBand stops
      // anything else being parked on top of it. All three matter for the same
      // reason: the date is cut into the stamp body, so a design that implies
      // otherwise is a design the factory can't make.
      const inset = a.w * 0.06;
      const size = this.#fitStraightFontSize(el.text, fontId, 400, TEXT_BASE_SIZE, a.w - 2 * inset);
      const band = new IText(el.text, {
        left: a.cx, top: a.cy, originX: 'center', originY: 'center',
        fontFamily: font.cssFamily, fontSize: size, fill: ink, textAlign: 'center',
        sdType: 'text', fontId, sdWeight: 400, sdDateBand: true,
        selectable: false, evented: false, editable: false, hasControls: false,
        hasBorders: false, lockMovementX: true, lockMovementY: true,
        lockScalingX: true, lockScalingY: true, lockRotation: true,
      });
      this.#canvas.add(band);
    } else if (el.kind === 'label') {
      // Edge-aligned text (used by the "for" designs: For… left, designation right).
      const inset = a.w * 0.06;
      const align = el.align || 'center';
      const originX = align === 'left' ? 'left' : align === 'right' ? 'right' : 'center';
      const x = align === 'left' ? a.cx - a.w / 2 + inset
        : align === 'right' ? a.cx + a.w / 2 - inset
          : a.cx;
      const y = el.yFrac != null ? a.cy - a.h / 2 + el.yFrac * a.h : a.cy;
      const size = this.#fitStraightFontSize(el.text, fontId, 400, TEXT_BASE_SIZE, a.w - 2 * inset);
      this.#canvas.add(new IText(el.text, { left: x, top: y, originX, originY: 'center', fontFamily: font.cssFamily, fontSize: size, fill: ink, textAlign: align, sdType: 'text', fontId, sdWeight: 400, sdUniform: true }));
    }
  }

  // --------------------------------------------------------------- proof ---
  openProof() {
    if (this.refs.proofOverlay) this.refs.proofOverlay.hidden = false;
    if (this.refs.proofImg) {
      try { this.refs.proofImg.src = this.#rasterize(360); } catch (e) { /* noop */ }
    }
    if (this.refs.proofSummary) this.refs.proofSummary.textContent = this.#summaryText();
  }
  closeProof() { if (this.refs.proofOverlay) this.refs.proofOverlay.hidden = true; }
  confirmProof() {
    this.#proofConfirmed = true;
    this.#emitAll();
    this.closeProof();
    if (this.refs.proofBtn) { this.refs.proofBtn.textContent = 'Proof reviewed ✓'; }
    this.dispatchEvent(new CustomEvent('stamp:proof-confirmed', { bubbles: true }));
  }

  /**
   * Demo/provision feature: emails the same manufacturing-grade vector SVG
   * real checkout would produce (#buildProductionSVG, unchanged/reused) to a
   * fixed test address via the vendored EmailJS SDK -- there's no backend in
   * this theme to send mail server-side. Entirely separate from the real
   * proof-confirm/add-to-cart pipeline; calling it has no effect on either.
   */
  async emailDesign() {
    const btn = this.refs.emailDesignBtn;
    const status = this.refs.emailDesignStatus;
    const setStatus = (text, cls) => {
      if (!status) return;
      status.textContent = text;
      status.hidden = !text;
      status.classList.remove('is-ok', 'is-error');
      if (cls) status.classList.add(cls);
    };
    // window.__SD_EMAIL_TEST_IDS is a QA-only override (set by qa/scripts/*
    // before clicking the button) so tests can exercise the real send/attach
    // logic against the stub without needing (or risking) real EmailJS
    // credentials in the repo. Always undefined on the live site.
    const testIds = (typeof window !== 'undefined' && window.__SD_EMAIL_TEST_IDS) || null;
    const serviceId = testIds?.serviceId || SD_EMAIL_SERVICE_ID;
    const templateId = testIds?.templateId || SD_EMAIL_TEMPLATE_ID;
    const publicKey = testIds?.publicKey || SD_EMAIL_PUBLIC_KEY;
    if (!serviceId || !templateId || !publicKey) {
      setStatus('Email sending isn’t configured yet (missing EmailJS IDs).', 'is-error');
      return;
    }
    if (!window.emailjs) {
      setStatus('Email service failed to load.', 'is-error');
      return;
    }
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    setStatus('', null);
    try {
      const svg = await this.#buildProductionSVG();
      const summary = this.#summaryText();

      // Delivery is svg_text (plain, copy/paste-able) only -- EmailJS's
      // file-attachment feature (sendForm + a dynamic attachment field) is
      // gated behind a paid plan, so it's not used here.
      await window.emailjs.send(serviceId, templateId, { summary, svg_text: svg }, publicKey);
      setStatus('Sent ✓', 'is-ok');
      setTimeout(() => setStatus('', null), 4000);
    } catch (err) {
      console.error('[stamp-designer] emailDesign failed', err);
      setStatus('Send failed, check console.', 'is-error');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = 'Email me the design file'; }
    }
  }

  #summaryText() {
    const n = this.#userObjects().length;
    const shape = this.#shape === 'round' ? 'Round' : 'Rectangle';
    const label = this.#size?.label || '';
    const parts = [];
    if (label) parts.push(label);
    if (!new RegExp(shape, 'i').test(label)) parts.push(shape);   // avoid "38mm (Round) · Round"
    parts.push(`${this.#ink.name} ink`, `${n} element${n === 1 ? '' : 's'}`);
    return parts.join(' · ');
  }

  // ------------------------------------------------------ raster proof img ---
  /**
   * A picture of the die, independent of how the shopper happens to be looking
   * at it.
   *
   * toDataURL renders through the live viewportTransform, so zooming in before
   * pressing Preview baked the zoom into the image -- measured, a 3x zoom took
   * ink touching the proof's border from 0 to 138 pixels and coverage from
   * 5.6% to 8.0%, i.e. the design was cut off. That image is what the shopper
   * sees in their cart and what rides the order as `_Proof image`, so it has to
   * be the whole die every time. The transform is put back afterwards so the
   * shopper's zoom survives the preview.
   */
  #rasterize(px) {
    const active = this.#canvas.getActiveObject();
    const viewport = this.#canvas.viewportTransform ? [...this.#canvas.viewportTransform] : null;
    this.#canvas.discardActiveObject();
    if (viewport) this.#canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    this.#canvas.requestRenderAll();
    try {
      return this.#canvas.toDataURL({ format: 'jpeg', quality: 0.72, multiplier: px / CANVAS });
    } finally {
      if (viewport) this.#canvas.setViewportTransform(viewport);
      if (active) this.#canvas.setActiveObject(active);
      this.#canvas.requestRenderAll();
    }
  }

  // =============================================== production SVG export ===
  async #emitAll() {
    try {
      const svg = await this.#buildProductionSVG();
      this.#setProp('_Design SVG', svg.length < PROP_LIMIT ? svg : this.#stripImages(svg));
      const proof = this.#rasterize(260);
      this.#setProp('_Proof image', proof.length < PROP_LIMIT ? proof : '');
      const scene = JSON.stringify(this.#canvas.toObject(EXTRA_PROPS));
      this.#setProp('_Design JSON', scene.length < PROP_LIMIT ? scene : '');
      this.#emitTextProps();
    } catch (e) {
      console.error('[stamp-designer] emit failed', e);
    }
  }

  #emitTextProps() {
    this.#setProp('Design mode', 'Custom design (studio)');
    this.#setProp('Ink Colour', this.#ink.name);
    if (this.#size?.label) this.#setProp('Size', this.#size.label);
    const fontsUsed = new Set();
    let ti = 0, ci = 0;
    this.#userObjects().forEach((o) => {
      if (o.sdType === 'text') { this.#setProp(`Text ${++ti}`, this.#applyToText(o)); fontsUsed.add(getFont(o.fontId).label); }
      else if (o.sdType === 'curvedText') { this.#setProp(`Curved text ${++ci}`, o.sdText || ''); fontsUsed.add(getFont(o.fontId).label); }
    });
    this.#setProp('Fonts', [...fontsUsed].join(', '));
    this.#setProp('Design summary', this.#summaryText());
  }
  #applyToText(o) { return (o.text || '').replace(/\n/g, ' / '); }

  /**
   * Build the manufacturing SVG.
   *
   * Hard rule for everything below: each element is a TOP-LEVEL <path> (or
   * <image>) holding absolute canvas coordinates, with no transform attribute
   * and no wrapping <g>. That shape is not a style preference -- it's the whole
   * reason this exporter exists instead of Fabric's toSVG().
   *
   * CorelDraw 13 (the shop's production tool) does not correctly compose
   * chained/nested transform matrices, and breakage scaled exactly with nesting
   * depth in three rounds of real testing: outlined text (one flat matrix)
   * always imported correctly, icons (matrix + an inner translate) came in as
   * blobs, and a border Group (four levels deep, three chained matrices) always
   * collapsed into a diagonal slash. So every object is baked down to the same
   * flat shape the text always survived as.
   *
   * Strokes become filled outlines for the same reason, and because a die is
   * cut from filled regions anyway -- explicit geometry beats a stroke width
   * the importer has to interpret. Clip paths are gone too: Corel imported the
   * die clip as a visible "PowerClip Curve" smeared across the design. Dropping
   * it is safe because the editor already keeps objects inside the die and the
   * physical die-cut crops to that boundary regardless.
   */
  async #buildProductionSVG() {
    const parts = [];
    for (const o of this.#userObjects()) parts.push(await this.#exportObject(o));
    return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"`
      + ` viewBox="0 0 ${CANVAS} ${CANVAS}" width="${CANVAS}" height="${CANVAS}">`
      + `${parts.join('')}</svg>`;
  }

  /** One canvas object -> flat, already-positioned SVG element(s). */
  async #exportObject(o) {
    if (o.sdType === 'text') return this.#outlineIText(o);
    if (o.sdType === 'curvedText') return this.#outlineCurved(o);
    if (o.sdType === 'image') return this.#exportImage(o);
    if (o instanceof Group) {
      // Border groups: flatten the children out rather than nesting them.
      // Each child's calcTransformMatrix() already folds in the group's own
      // transform, so they land in the right place with nothing wrapping them.
      const out = [];
      for (const child of o.getObjects()) {
        if (child.sdCenter) continue;            // invisible sizing puck
        out.push(await this.#exportObject(child));
      }
      return out.join('');
    }
    return this.#exportShape(o);
  }

  /** Rect / Circle / Line / Path (icon) -> filled, transform-free path(s). */
  #exportShape(o) {
    const m = o.calcTransformMatrix();
    const fill = this.#hex(o.fill);
    const stroke = this.#hex(o.stroke);
    const sw = o.strokeWidth || 0;

    // The shape's own outline, in its local (unscaled, centre-origin) space --
    // the matrix above is what puts it on the canvas.
    let body = null;
    if (o instanceof Path) body = this.#pathCommands(o);
    else if (o instanceof Circle) body = this.#circleCommands(o.radius);
    else if (o instanceof Line) {
      const p = o.calcLinePoints();               // NOT x1/y1/x2/y2: those drift after a move
      body = [['M', p.x1, p.y1], ['L', p.x2, p.y2]];
    } else if (o instanceof Rect) body = this.#rectCommands(o.width, o.height, o.rx || 0, o.ry || 0);
    if (!body || !body.length) return '';

    const out = [];
    if (fill) out.push(`<path d="${this.#bake(body, m)}" fill="${fill}"/>`);
    if (!stroke || sw <= 0) return out.join('');

    // strokeUniform (borders, lines) pins the stroke to a fixed canvas width,
    // so convert it into local units before the matrix scales it back up.
    const [sx, sy] = this.#matrixScale(m);
    const avg = ((sx + sy) / 2) || 1;
    const localSw = o.strokeUniform ? sw / avg : sw;

    // Dashed strokes stay real strokes: only the ring-dashed border preset uses
    // them, and rebuilding a dash pattern as filled arc segments isn't worth it
    // for one preset. Still flat and transform-free.
    if (o.strokeDashArray && o.strokeDashArray.length) {
      out.push(`<path d="${this.#bake(body, m)}" fill="none" stroke="${stroke}"`
        + ` stroke-width="${this.#r(o.strokeUniform ? sw : sw * avg)}"`
        + ` stroke-dasharray="${o.strokeDashArray.map((n) => this.#r(n * (o.strokeUniform ? 1 : avg))).join(',')}"/>`);
      return out.join('');
    }

    const ring = this.#strokeOutline(o, localSw);
    if (ring) out.push(`<path d="${this.#bake(ring, m)}" fill="${stroke}"/>`);
    else {
      // Only reachable if an icon path ever gains a stroke -- offsetting an
      // arbitrary curve is out of scope, so keep a plain (still flat) stroke.
      out.push(`<path d="${this.#bake(body, m)}" fill="none" stroke="${stroke}"`
        + ` stroke-width="${this.#r(o.strokeUniform ? sw : sw * avg)}"/>`);
    }
    return out.join('');
  }

  /**
   * A stroked shape re-expressed as the filled region the stroke covers.
   * Outer and inner subpaths are wound in OPPOSITE directions so the hole
   * renders under both nonzero and evenodd fill rules -- deliberately not
   * relying on a fill-rule attribute the importer might ignore.
   */
  #strokeOutline(o, sw) {
    if (o instanceof Line) {
      const p = o.calcLinePoints();
      const dx = p.x2 - p.x1, dy = p.y2 - p.y1;
      const len = Math.hypot(dx, dy);
      if (!len) return null;
      const nx = (-dy / len) * (sw / 2), ny = (dx / len) * (sw / 2);
      return [['M', p.x1 + nx, p.y1 + ny], ['L', p.x2 + nx, p.y2 + ny],
        ['L', p.x2 - nx, p.y2 - ny], ['L', p.x1 - nx, p.y1 - ny], ['Z']];
    }
    if (o instanceof Circle) {
      const outer = this.#circleCommands(o.radius + sw / 2);
      const ri = o.radius - sw / 2;
      return ri > 0 ? outer.concat(this.#reverseCommands(this.#circleCommands(ri))) : outer;
    }
    if (o instanceof Rect) {
      const rx = o.rx || 0, ry = o.ry || 0;
      const outer = this.#rectCommands(o.width + sw, o.height + sw,
        rx ? rx + sw / 2 : 0, ry ? ry + sw / 2 : 0);
      const iw = o.width - sw, ih = o.height - sw;
      if (iw <= 0 || ih <= 0) return outer;
      const inner = this.#rectCommands(iw, ih,
        rx ? Math.max(0, rx - sw / 2) : 0, ry ? Math.max(0, ry - sw / 2) : 0);
      return outer.concat(this.#reverseCommands(inner));
    }
    return null;
  }

  #exportImage(o) {
    const src = (o.getSrc && o.getSrc()) || (o._element && o._element.src) || '';
    if (!src) return '';
    const m = o.calcTransformMatrix();
    // Axis-aligned (the normal case): bake scale and position straight into
    // x/y/width/height so the element carries no transform at all. A rotated
    // image is the one thing that can't be expressed that way, so it keeps a
    // SINGLE flat matrix -- never nested, same shape the text always survived as.
    if (!o.angle && !o.skewX && !o.skewY) {
      const [sx, sy] = this.#matrixScale(m);
      const dw = o.width * sx, dh = o.height * sy;
      return `<image x="${this.#r(m[4] - dw / 2)}" y="${this.#r(m[5] - dh / 2)}"`
        + ` width="${this.#r(dw)}" height="${this.#r(dh)}" xlink:href="${src}"/>`;
    }
    return `<image x="${this.#r(-o.width / 2)}" y="${this.#r(-o.height / 2)}"`
      + ` width="${this.#r(o.width)}" height="${this.#r(o.height)}"`
      + ` transform="matrix(${m.map((n) => this.#r(n)).join(' ')})" xlink:href="${src}"/>`;
  }

  // ------------------------------------------- geometry -> path commands ---
  // Commands are [letter, x, y, ...] with every coordinate as an (x, y) pair,
  // which is what both Fabric's simplified path data and opentype's commands
  // reduce to -- so #bake can walk any of them the same way.

  /** Fabric Path -> commands in centre-origin space (its own pathOffset removed). */
  #pathCommands(o) {
    const ox = o.pathOffset ? o.pathOffset.x : 0;
    const oy = o.pathOffset ? o.pathOffset.y : 0;
    return (o.path || []).map((seg) => {
      const out = [seg[0]];
      for (let i = 1; i < seg.length; i += 2) out.push(seg[i] - ox, seg[i + 1] - oy);
      return out;
    });
  }

  /** opentype Path -> commands (arcs are already cubics at this point). */
  #otCommands(p) {
    const out = [];
    for (const c of p.commands) {
      if (c.type === 'M' || c.type === 'L') out.push([c.type, c.x, c.y]);
      else if (c.type === 'C') out.push(['C', c.x1, c.y1, c.x2, c.y2, c.x, c.y]);
      else if (c.type === 'Q') out.push(['Q', c.x1, c.y1, c.x, c.y]);
      else if (c.type === 'Z') out.push(['Z']);
    }
    return out;
  }

  #circleCommands(r) {
    const k = 0.5522847498307936 * r;
    return [['M', r, 0],
      ['C', r, k, k, r, 0, r],
      ['C', -k, r, -r, k, -r, 0],
      ['C', -r, -k, -k, -r, 0, -r],
      ['C', k, -r, r, -k, r, 0],
      ['Z']];
  }

  #rectCommands(w, h, rx, ry) {
    const x = w / 2, y = h / 2;
    const cx = Math.min(Math.max(rx, 0), x), cy = Math.min(Math.max(ry, 0), y);
    if (!cx || !cy) {
      return [['M', -x, -y], ['L', x, -y], ['L', x, y], ['L', -x, y], ['Z']];
    }
    const kx = cx * 0.5522847498307936, ky = cy * 0.5522847498307936;
    return [['M', -x + cx, -y],
      ['L', x - cx, -y],
      ['C', x - cx + kx, -y, x, -y + cy - ky, x, -y + cy],
      ['L', x, y - cy],
      ['C', x, y - cy + ky, x - cx + kx, y, x - cx, y],
      ['L', -x + cx, y],
      ['C', -x + cx - kx, y, -x, y - cy + ky, -x, y - cy],
      ['L', -x, -y + cy],
      ['C', -x, -y + cy - ky, -x + cx - kx, -y, -x + cx, -y],
      ['Z']];
  }

  /** Flip one closed subpath's winding (outer/inner ring must oppose). */
  #reverseCommands(cmds) {
    const pts = [], segs = [];
    for (const s of cmds) {
      if (s[0] === 'M') pts.push([s[1], s[2]]);
      else if (s[0] === 'L') { pts.push([s[1], s[2]]); segs.push(['L']); }
      else if (s[0] === 'Q') { pts.push([s[3], s[4]]); segs.push(['Q', s[1], s[2]]); }
      else if (s[0] === 'C') { pts.push([s[5], s[6]]); segs.push(['C', s[1], s[2], s[3], s[4]]); }
    }
    if (!pts.length) return cmds;
    const last = pts[pts.length - 1];
    const out = [['M', last[0], last[1]]];
    for (let i = segs.length - 1; i >= 0; i -= 1) {
      const s = segs[i], p = pts[i];
      if (s[0] === 'L') out.push(['L', p[0], p[1]]);
      else if (s[0] === 'Q') out.push(['Q', s[1], s[2], p[0], p[1]]);
      else out.push(['C', s[3], s[4], s[1], s[2], p[0], p[1]]);   // control points swap
    }
    out.push(['Z']);
    return out;
  }

  /** Commands + matrix -> absolute path data. This is what removes transforms. */
  #bake(cmds, m) {
    const [a, b, c, d, e, f] = m;
    let out = '';
    for (const seg of cmds) {
      const nums = [];
      for (let i = 1; i < seg.length; i += 2) {
        const x = seg[i], y = seg[i + 1];
        nums.push(this.#r(a * x + c * y + e), this.#r(b * x + d * y + f));
      }
      out += seg[0] + nums.join(' ');
    }
    return out;
  }

  #matrixScale(m) { return [Math.hypot(m[0], m[1]), Math.hypot(m[2], m[3])]; }

  /** Normalise a Fabric colour to hex ('' when there's nothing to paint). */
  #hex(v) {
    if (!v || v === 'none' || v === 'transparent') return '';
    const s = String(v).trim();
    if (s[0] === '#') return s;
    const rgb = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i);
    if (!rgb) return s;
    if (rgb[4] !== undefined && Number(rgb[4]) === 0) return '';       // fully transparent
    const h = (n) => Math.round(Number(n)).toString(16).padStart(2, '0');
    return `#${h(rgb[1])}${h(rgb[2])}${h(rgb[3])}`;
  }

  #stripImages(svg) { return svg.replace(/<image\b[^>]*>/g, ''); }

  // --- opentype outlining of a plain IText object ---------------------------
  async #loadOtFont(fontId, weight) {
    const key = `${fontId}-${weight}`;
    if (this.#otCache.has(key)) return this.#otCache.get(key);
    const url = resolveTtfUrl(this.#assets.fonts || {}, fontId, weight);
    if (!url) return null;
    let buf = await fetch(url).then((r) => r.arrayBuffer());
    // Shopify's CDN transcodes real .ttf/.otf assets to woff2 (which opentype
    // can't parse), so the theme ships each TTF as base64 inside a .json asset
    // it leaves untouched. Detect a raw sfnt signature (mock/local) vs. that
    // base64-JSON payload (live theme) and decode as needed.
    const s = new Uint8Array(buf.slice(0, 4));
    const isSfnt = (s[0] === 0 && s[1] === 1 && s[2] === 0 && s[3] === 0)   // TrueType
      || (s[0] === 0x4F && s[1] === 0x54 && s[2] === 0x54 && s[3] === 0x4F) // 'OTTO'
      || (s[0] === 0x74 && s[1] === 0x72 && s[2] === 0x75 && s[3] === 0x65) // 'true'
      || (s[0] === 0x74 && s[1] === 0x74 && s[2] === 0x63 && s[3] === 0x66); // 'ttcf'
    if (!isSfnt) buf = this.#b64ToBuf(JSON.parse(new TextDecoder().decode(buf)).b64);
    const font = (opentype.parse || opentype._parse)(buf);
    this.#otCache.set(key, font);
    return font;
  }
  #b64ToBuf(b64) {
    const bin = atob(b64);
    const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) u8[i] = bin.charCodeAt(i);
    return u8.buffer;
  }

  async #outlineIText(o) {
    const weight = (o.sdWeight || o.fontWeight) >= 700 ? 700 : 400;
    const font = await this.#loadOtFont(o.fontId, weight);
    if (!font) return o.toSVG();                 // fallback: keep <text>
    const fs = o.fontSize;
    const lines = (o.text || '').split('\n');
    const leftOffset = -o.width / 2, topOffset = -o.height / 2;
    const lineH = o.getHeightOfLine(0);
    const ascent = (font.ascender / font.unitsPerEm) * fs;
    const csPx = (o.charSpacing || 0) / 1000 * fs;
    const cmds = [];
    lines.forEach((line, li) => {
      // per-line advance width (with charSpacing) for alignment
      let lw = 0;
      for (const ch of line) lw += font.getAdvanceWidth(ch, fs) + csPx;
      lw = Math.max(0, lw - csPx);
      let x;
      if (o.textAlign === 'left') x = leftOffset;
      else if (o.textAlign === 'right') x = leftOffset + o.width - lw;
      else x = -lw / 2;
      const baseline = topOffset + li * lineH + (lineH - fs) / 2 + ascent;
      for (const ch of line) {
        cmds.push(...this.#otCommands(font.getPath(ch, x, baseline, fs)));
        x += font.getAdvanceWidth(ch, fs) + csPx;
      }
    });
    if (!cmds.length) return '';
    // Every glyph shares this object's matrix, so one bake covers the lot.
    return `<path d="${this.#bake(cmds, o.calcTransformMatrix())}" fill="${this.#ink.hex}"/>`;
  }

  async #outlineCurved(o) {
    const weight = (o.sdWeight || 400) >= 700 ? 700 : 400;
    const font = await this.#loadOtFont(o.fontId, weight);
    if (!font || !o.sdGlyphs) return o.toSVG();
    const fs = o.sdFontSize || 30;
    const ascent = (font.ascender / font.unitsPerEm) * fs;
    const height = fs * 1.16;
    const cyoff = ascent - height / 2;      // baseline offset from Fabric's line-box centre
    const gm = o.calcTransformMatrix();
    // Each glyph has its own rotation, so each is baked with its own matrix --
    // then all of them are concatenated into ONE flat path. Subpaths are free;
    // per-glyph transform attributes are exactly what breaks in CorelDraw.
    let d = '';
    for (const g of o.sdGlyphs) {
      const adv = font.getAdvanceWidth(g.char, fs);
      const rad = (g.deg * Math.PI) / 180;
      const R = [Math.cos(rad), Math.sin(rad), -Math.sin(rad), Math.cos(rad), 0, 0];
      const Tpos = [1, 0, 0, 1, g.x, g.y];
      const Tc = [1, 0, 0, 1, -adv / 2, cyoff];
      let m = util.multiplyTransformMatrices(R, Tc);
      m = util.multiplyTransformMatrices(Tpos, m);
      m = util.multiplyTransformMatrices(gm, m);
      const cmds = this.#otCommands(font.getPath(g.char, 0, 0, fs));
      if (cmds.length) d += this.#bake(cmds, m);
    }
    return d ? `<path d="${d}" fill="${this.#ink.hex}"/>` : '';
  }

  #r(n) { return Math.abs(n) < 1e-4 ? 0 : Math.round(n * 1000) / 1000; }

  // ----------------------------------------------------- upload (B&W) ---
  /** Show the upload guidelines/welcome card in place of the toolbar+canvas. */
  startUploadFlow() {
    if (this.refs.uploadIntro) this.refs.uploadIntro.hidden = false;
    if (this.refs.editorMain) this.refs.editorMain.hidden = true;
  }
  #hideUploadIntro() {
    if (this.refs.uploadIntro) this.refs.uploadIntro.hidden = true;
    if (this.refs.editorMain) this.refs.editorMain.hidden = false;
  }
  /** "Start with text instead" -- bail out of the intro without uploading. */
  skipUploadIntro() { this.#hideUploadIntro(); }

  pickUpload() { this.refs.uploadInput?.click(); }

  onUploadChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    this.#hideUploadIntro();
    const reader = new FileReader();
    reader.onload = () => this.#placeUpload(String(reader.result));
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async #placeUpload(dataUrl) {
    const processed = await this.#processImage(dataUrl, 128, 'threshold');
    const img = await FabricImage.fromURL(processed);
    img.set({
      left: this.#area.cx, top: this.#area.cy, originX: 'center', originY: 'center',
      sdType: 'image', sdOrigSrc: dataUrl, sdThreshold: 128, sdMode: 'threshold', sdUniform: true,
    });
    img.scaleToWidth(Math.min(this.#area.w, this.#area.h) * 0.42);
    this.#addObject(img, true);
  }

  /** Convert an image to 1-bit ink-on-transparent art. Returns a PNG data URL. */
  #processImage(srcUrl, threshold, mode) {
    return new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        const max = 600;
        const s = Math.min(1, max / Math.max(image.width, image.height));
        const w = Math.max(1, Math.round(image.width * s));
        const h = Math.max(1, Math.round(image.height * s));
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        const ctx = cv.getContext('2d');
        ctx.drawImage(image, 0, 0, w, h);
        let data;
        try { data = ctx.getImageData(0, 0, w, h); }
        catch (err) { resolve(srcUrl); return; }     // tainted (e.g. SVG) — keep as-is
        const px = data.data;
        const [ir, ig, ib] = this.#hexRgb(this.#ink.hex);
        for (let i = 0; i < px.length; i += 4) {
          const a = px[i + 3];
          const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
          // silhouette: any non-transparent, non-white pixel becomes solid ink (so a
          // logo on an opaque white background still yields a shape, not a filled block).
          const on = mode === 'silhouette' ? (a > 24 && lum < 245) : (a > 24 && lum < threshold);
          if (on) { px[i] = ir; px[i + 1] = ig; px[i + 2] = ib; px[i + 3] = 255; }
          else { px[i + 3] = 0; }
        }
        ctx.putImageData(data, 0, 0);
        resolve(cv.toDataURL('image/png'));
      };
      image.onerror = () => resolve(srcUrl);
      image.src = srcUrl;
    });
  }

  async #regenImage(o) {
    if (!o || !o.sdOrigSrc) return;
    const url = await this.#processImage(o.sdOrigSrc, o.sdThreshold ?? 128, o.sdMode || 'threshold');
    await o.setSrc(url);
    this.#canvas.requestRenderAll();
  }

  async onThresholdChange(e) {
    const o = this.#canvas.getActiveObject(); if (!o || o.sdType !== 'image') return;
    o.sdThreshold = parseInt(e.target.value, 10) || 128;
    await this.#regenImage(o); this.#after();
  }
  async onModeChange(e) {
    const o = this.#canvas.getActiveObject(); if (!o || o.sdType !== 'image') return;
    o.sdMode = e.target.value;
    await this.#regenImage(o); this.#after();
  }

  #hexRgb(hex) {
    const m = hex.replace('#', '');
    return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
  }

  // ------------------------------------------------------- curved text ---
  addCurvedText() {
    const base = this.#shape === 'round' ? this.#area.radius : Math.min(this.#area.w, this.#area.h) / 2;
    const g = this.#shape === 'rectangle'
      ? this.#buildCurvedGroup({
        sdText: 'COMPANY NAME', fontId: SD_FONTS[0].id, sdWeight: 400,
        sdFontSize: 36, sdArc: 'top', sdRadius: this.#curveRange().min, sdFlatten: 0.5,
      })
      : this.#buildCurvedGroup({
        sdText: 'COMPANY NAME', fontId: SD_FONTS[0].id, sdWeight: 400,
        sdFontSize: 36, sdArc: 'top', sdRadius: base * 0.82,
      });
    g.set({ left: this.#area.cx, top: this.#area.cy });
    this.#addObject(g, true);
  }

  #measureCtx() {
    if (!this.#mctx) this.#mctx = document.createElement('canvas').getContext('2d');
    return this.#mctx;
  }

  #curvedLayout(props) {
    const font = getFont(props.fontId);
    const ctx = this.#measureCtx();
    ctx.font = `${props.sdWeight} ${props.sdFontSize}px '${font.cssFamily}'`;
    const chars = [...(props.sdText || '')];
    const csPx = ((props.sdSpacing || 0) / 1000) * props.sdFontSize;   // letter-spacing
    const widths = chars.map((c) => (ctx.measureText(c).width || props.sdFontSize * 0.5) + csPx);
    const total = widths.reduce((a, b) => a + b, 0) || 1;
    const R = props.sdRadius;
    const totalAngle = total / R;
    const bottom = props.sdArc === 'bottom';
    const centerAngle = bottom ? Math.PI : 0;
    const sign = bottom ? -1 : 1;
    let cum = 0;
    const glyphs = [];
    chars.forEach((c, i) => {
      const w = widths[i];
      const off = -totalAngle / 2 + (cum + w / 2) / R;
      const theta = centerAngle + sign * off;
      glyphs.push({ char: c, x: R * Math.sin(theta), y: -R * Math.cos(theta), deg: (theta * 180) / Math.PI + (bottom ? 180 : 0), w });
      cum += w;
    });
    return glyphs;
  }

  /** Straight-line sibling of #curvedLayout, used only to blend rectangle
   *  curved text toward flat (see #buildCurvedGroup's sdFlatten handling).
   *  Same per-glyph width measurement, laid out along a centred horizontal
   *  line instead of an arc; deg is a single constant (0, or 180 for a
   *  bottom-arc string so it flattens toward the same upright-when-read
   *  orientation), not per-glyph, matching a straight line having no
   *  rotation variation across glyphs. */
  #curvedLayoutFlat(props) {
    const font = getFont(props.fontId);
    const ctx = this.#measureCtx();
    ctx.font = `${props.sdWeight} ${props.sdFontSize}px '${font.cssFamily}'`;
    const chars = [...(props.sdText || '')];
    const csPx = ((props.sdSpacing || 0) / 1000) * props.sdFontSize;
    const widths = chars.map((c) => (ctx.measureText(c).width || props.sdFontSize * 0.5) + csPx);
    const total = widths.reduce((a, b) => a + b, 0) || 1;
    const bottom = props.sdArc === 'bottom';
    let cum = 0;
    return chars.map((c, i) => {
      const w = widths[i];
      const x = -total / 2 + cum + w / 2;
      cum += w;
      return { char: c, x, y: 0, deg: bottom ? 180 : 0, w };
    });
  }

  #buildCurvedGroup(props) {
    const font = getFont(props.fontId);
    // Round only: clamp the font size so the arc's outward reach (radius +
    // ascent) never exceeds the die's clip circle -- otherwise the tallest
    // glyphs get visually clipped at their outer tip (worst-looking at the
    // top, against the border ring), confirmed by reproducing it directly at
    // an enlarged Size. Runs here so it covers every caller (template
    // placement, addCurvedText, and the Size-field -> #rebuildCurved path,
    // which previously had no clamp at all).
    const sdFontSize = (this.#shape === 'round' && this.#area)
      ? this.#fitRingRadialSize(props.sdText, props.fontId, props.sdWeight, props.sdFontSize, props.sdRadius, props.sdArc)
      : props.sdFontSize;
    const layoutProps = sdFontSize === props.sdFontSize ? props : { ...props, sdFontSize };
    let glyphs = this.#curvedLayout(layoutProps);
    let bgRadius = props.sdRadius;
    // Rectangle only: blend each glyph toward a straight-line layout by
    // sdFlatten (0 = fully circular at sdRadius, 1 = fully flat). Both
    // endpoints are concrete, already-understood layouts, so a per-glyph
    // lerp on (x, y, deg) is safe -- confirmed growing sdRadius itself does
    // NOT flatten text in place, since the glyph y-formula anchors it on the
    // circle's far edge, so a bigger radius just moves the block away.
    if (this.#shape === 'rectangle' && props.sdFlatten > 0) {
      const flatGlyphs = this.#curvedLayoutFlat(layoutProps);
      const t = Math.max(0, Math.min(1, props.sdFlatten));
      glyphs = glyphs.map((g, i) => {
        const f = flatGlyphs[i];
        return { char: g.char, x: g.x + (f.x - g.x) * t, y: g.y + (f.y - g.y) * t, deg: g.deg + (f.deg - g.deg) * t, w: g.w };
      });
      // Background circle (below) stabilizes the group's selection bounding
      // box -- widen it so the flat line at the far end of the slider is
      // never wider than the box the handles sit on.
      const flatHalfWidth = Math.max(0, ...flatGlyphs.map((g) => Math.abs(g.x) + g.w / 2));
      bgRadius = Math.max(props.sdRadius, flatHalfWidth + 12);
    }
    const kids = [new Circle({ radius: bgRadius, left: 0, top: 0, originX: 'center', originY: 'center', fill: 'rgba(0,0,0,0)', sdCenter: true, selectable: false, evented: false })];
    glyphs.forEach((g) => {
      kids.push(new IText(g.char, {
        left: g.x, top: g.y, originX: 'center', originY: 'center', angle: g.deg,
        fontFamily: font.cssFamily, fontWeight: props.sdWeight, fontSize: sdFontSize,
        fill: this.#ink.hex, editable: false, selectable: false, evented: false,
      }));
    });
    const group = new Group(kids, {
      originX: 'center', originY: 'center', subTargetCheck: false, sdUniform: true,
      perPixelTargetFind: this.#shape === 'round',
      sdType: 'curvedText', sdText: props.sdText, fontId: props.fontId, sdWeight: props.sdWeight,
      sdFontSize, sdArc: props.sdArc, sdRadius: props.sdRadius,
      sdSpacing: props.sdSpacing || 0, sdFlatten: props.sdFlatten || 0, sdGlyphs: glyphs,
    });
    // Fabric's Group constructor silently renormalizes children's coordinates
    // while computing its own bounds -- verified directly (isolated repro
    // against this vendored build, independent of layoutManager/FixedLayout)
    // that this shrinks every glyph to ~94% of the intended sdRadius, and
    // non-uniformly (worst at the arc's centre), which is what actually made
    // ring text look pulled in off the ring's true centre. Re-applying each
    // glyph's already-computed position after construction lands every one
    // of them exactly back on sdRadius.
    const textKids = group.getObjects().filter((o) => o.text !== undefined);
    textKids.forEach((k, i) => k.set({ left: glyphs[i].x, top: glyphs[i].y }));
    group.setCoords();
    return group;
  }

  /**
   * Push a dragged object clear of the date band.
   *
   * Nothing on a dater may sit across the wheels -- the band is a gap in the
   * die, so ink laid over it simply isn't cut, and a proof that shows text
   * there is promising something the stamp cannot print. The band itself is
   * unselectable, so the only way anything lands on it is another element being
   * dragged or scaled over it; this sends that element back to whichever side
   * it came from, which is the one that needs the least correction.
   *
   * @param {object|null|undefined} obj
   */
  #keepOffDateBand(obj) {
    if (!obj || obj.sdDateBand) return;
    const band = this.#canvas.getObjects().find((o) => o.sdDateBand);
    if (!band) return;

    const pad = 6;
    const bb = band.getBoundingRect(true);
    const top = bb.top - pad;
    const bottom = bb.top + bb.height + pad;

    const r = obj.getBoundingRect(true);
    if (r.top >= bottom || r.top + r.height <= top) return;   // already clear

    // Whichever side its centre is on is the side it belongs on.
    const centre = r.top + r.height / 2;
    const bandCentre = bb.top + bb.height / 2;
    const targetTop = centre < bandCentre ? top - r.height : bottom;
    obj.set('top', obj.top + (targetTop - r.top));
    obj.setCoords();
  }

  /**
   * Fold a hand-resize of round arc text back into its own size and radius.
   *
   * Dragging a corner handle only sets scaleX/scaleY, which #rebuildCurved
   * carries over untouched -- so the radial clamp, which runs on sdFontSize,
   * never saw the resize and the text could be dragged straight through the
   * border ring however carefully it was fitted when it was built.
   *
   * Only the type size is taken from the drag, not the radius. Scaling the
   * radius too is the obvious reading of a corner handle and it is wrong here:
   * measured directly, a 1.4x drag moved a 168px arc out to 236px, past the
   * ring it belongs between and past the die itself, at which point the clamp
   * had no room left and collapsed the text to its 11px floor. The ring an arc
   * sits on is set by the design; the handle sizes the letters on it.
   *
   * @param {object|null|undefined} obj
   */
  #bakeCurvedScale(obj) {
    if (!obj || obj.sdType !== 'curvedText' || this.#shape !== 'round') return;
    const s = obj.scaleX;
    if (!Number.isFinite(s) || Math.abs(s - 1) < 0.001 || Math.abs(obj.scaleY - s) > 0.001) return;
    obj.sdFontSize = obj.sdFontSize * s;
    obj.set({ scaleX: 1, scaleY: 1 });
    this.#rebuildCurved(obj);
  }

  #rebuildCurved(o) {
    const { left, top, scaleX, scaleY, angle } = o;
    const fresh = this.#buildCurvedGroup({
      sdText: o.sdText, fontId: o.fontId, sdWeight: o.sdWeight,
      sdFontSize: o.sdFontSize || 30, sdArc: o.sdArc || 'top', sdRadius: o.sdRadius,
      sdSpacing: o.sdSpacing || 0, sdFlatten: o.sdFlatten || 0,
    });
    fresh.set({ left, top, scaleX, scaleY, angle });
    fresh.clipPath = this.#clipFor(fresh);
    this.#canvas.remove(o);
    this.#canvas.add(fresh);
    this.#sinkGuides();
    this.#canvas.setActiveObject(fresh);
    this.#canvas.requestRenderAll();
  }

  onCurveTextChange(e) {
    const o = this.#canvas.getActiveObject(); if (!o || o.sdType !== 'curvedText') return;
    o.sdText = e.target.value; this.#rebuildCurved(o); this.#after();
  }

  // ----------------------------------------------------- hidden cart props ---
  #setProp(name, value) {
    if (!this.#propInputs) this.#propInputs = new Map();
    let input = this.#propInputs.get(name);
    if (value) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        const formId = this.dataset.productFormId;
        if (formId) input.setAttribute('form', formId);
        this.appendChild(input);
        this.#propInputs.set(name, input);
      }
      input.name = `properties[${name}]`;
      input.value = value;
    } else if (input) {
      input.removeAttribute('name');
      input.value = '';
    }
  }

  #scheduleEmit() {
    if (!this.#proofConfirmed) return;   // heavy props only matter once proofed
    clearTimeout(this.#emitTimer);
    this.#emitTimer = setTimeout(() => this.#emitAll(), 250);
  }
}

if (!customElements.get('stamp-designer-component')) {
  customElements.define('stamp-designer-component', StampDesigner);
}
